import HttpCodes from 'http-status-codes';
import i18next from 'i18next';
import _ from 'lodash';
import moment from 'moment-timezone';
import tinycolor from 'tinycolor2';
import tinygradient from 'tinygradient';
import { addAnnotation, fetchAnnotations } from '@/annotation/annotation.actions';
import { DEBOUNCE } from '@/core/core.constants';
import {
  sqCapsuleRuntimeStore,
  sqDurationStore,
  sqInvestigateStore,
  sqItemRuntimeStore,
  sqProfileSearchStore,
  sqScorecardStore,
  sqTrendCapsuleStore,
  sqTrendConditionStore,
  sqTrendMetricStore,
  sqTrendSeriesStore,
  sqTrendStore,
  sqWorkbenchStore,
  sqWorkbookStore,
  sqWorksheetStore,
} from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import { computeLightestColor } from '@/core/html.utilities';
import { getMaxSeriesPixels } from '@/core/utilities';
import { getCapsuleFormula } from '@/datetime/dateTime.utilities';
import { API_TYPES, NUMBER_CONVERSIONS } from '@/main/app.constants';
import { fetchMonitoredConditionIds } from '@/notifications/notifications.actions';
import { CANCELLATION_GROUP_GUID_SEPARATOR, cancelAll, cancelGroup, count } from '@/requests/pendingRequests.utilities';
import { fetchMinimapSignals, fetchPlot, refreshScatterPlotView } from '@/scatterPlot/scatterPlot.actions';
import { clearSelection } from '@/search/search.actions';
import { getHierarchyItemTrendProps, getScalingTableItemTrendProps } from '@/search/searchResult.utilities.service';
import { headlessCaptureMetadata, headlessRenderMode } from '@/services/headlessCapture.utilities';
import { emitPermissions } from '@/services/notifier.service';
import { refreshStatsData } from '@/summaryReport/summaryReport.actions';
import { fetchTable } from '@/tableBuilder/tableBuilder.actions';
import {
  clearInvestigatedItem,
  closeInvestigationTool,
  updateDerivedDataTree,
} from '@/toolSelection/investigate.actions';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { takeCapsulesFromSelection } from '@/tools/manualCondition/capsuleGroup.utilities';
import { conditionFormula } from '@/tools/manualCondition/conditionFormula.service';
import { triggerManualConditionPreviewDebounced } from '@/tools/manualCondition/manualCondition.actions';
import { getTemplateInstanceIdFromProps } from '@/tools/models/templateInstance.utilities';
import { TRACK } from '@/track/track.constants';
import { doTrack } from '@/track/trackProxy.service';
import { fetchTreemap } from '@/treemap/treemap.actions';
import {
  findChildrenIn,
  findItemIn,
  getAlignableItems,
  getAllChildItems,
  getAllItems,
  getItemId,
  getItemType,
  getTrendStores,
  hasPreviousScalingTableInfo,
  isCompositeChild,
  isItemFromModelApplication,
} from '@/trend/trendDataHelper.utilities';
import { CHART_THRESHOLDS } from '@/trend/trendViewer/trendViewer.constants';
import {
  recomputeRuntimeChartItems,
  refreshRuntimeUncertainItems,
  writeRuntimeChartCapsules,
  writeRuntimeStitchBreaks,
  writeRuntimeStitchDetails,
} from '@/trendData/capsuleRuntime.actions';
import { getChartWidth as getStoredChartWidth, recordChartWidth } from '@/trendData/chartWidth.utilities';
import { displayRange } from '@/trendData/duration.actions';
import { getDurationDisplayRange } from '@/trendData/duration.utilities';
import {
  getFrozenYAxisCustomizations,
  getItemDataStatus,
  recordDataStatus,
  stampLastFetchRequest,
} from '@/trendData/itemRuntime.utilities';
import { beginArrivalFetch, isBackendRowsLimitError, runArrivalCommit } from '@/trendData/trend.utilities';
import { addCapsulesToTrendStores } from '@/trendData/trendCapsule.actions';
import { findCapsuleItem, getCapsuleItems } from '@/trendData/trendCapsule.selectors';
import { removeChildren } from '@/trendData/trendChildItem.actions';
import { setTrendColorMode } from '@/trendData/trendColor.actions';
import {
  addCondition,
  fetchAllTimebarCapsules,
  fetchTimebarCapsules,
  setConditionDataStatusHiddenFromTrend,
  setConditionDataStatusLoading,
  setConditionDataStatusPresent,
} from '@/trendData/trendCondition.actions';
import {
  collectReindexInfoBeforeRemoval,
  performReindexingAfterRemoval,
} from '@/trendData/trendConditionLayout.utilities';
import {
  getEnabledColumns,
  removeColumnFilter,
  removePropertiesColumn,
  setColumnEnabled,
  setColumnFilter,
  setCustomizationMode,
  setGridlines,
  setLabelDisplayConfiguration,
  setPanelSort,
  setShowSelectedRegionOptions,
  updateCustomLabelTargets,
} from '@/trendData/trendCustomization.actions';
import {
  API_TYPES_TO_ITEM_TYPES,
  BAR_CHART_LINE_WIDTHS,
  CAPSULES_PER_PAGE,
  CAPSULE_PANEL_LOOKUP_COLUMNS,
  CHART_CAPSULES_LIMIT,
  CapsuleTimeColorMode,
  FORMULA_FRAGMENT_TYPE,
  ITEM_CHILDREN_TYPES,
  ITEM_DATA_STATUS,
  ITEM_TYPES,
  ITEM_TYPES_WITH_COLUMNS,
  LABEL_LOCATIONS,
  LINE_WIDTHS,
  PREVIEW_ID,
  SAMPLE_OPTIONS,
  TREND_CONDITION_STATS,
  TREND_PANELS,
  TREND_SIGNAL_STATS,
  TREND_VIEWS,
} from '@/trendData/trendData.constants';
import {
  removeItemsFromTrendStores,
  setColorInTrendStores,
  setCustomizationsInTrendStores,
  setDataStatusAbortedInTrendStores,
  setDataStatusCanceledInTrendStores,
  setDataStatusCanceledRetriesInTrendStores,
  setDataStatusFailureInTrendStores,
  setDataStatusNotRequiredInTrendStores,
  setDataStatusRedactedInTrendStores,
  setPropertiesInTrendStores,
  setSelectedCapsulesInTrendStores,
  setSelectedInTrendStores,
  swapItemsInTrendStores,
} from '@/trendData/trendItemStores.actions';
import {
  addLegacyBoundaries,
  addMetric,
  addMetricChildren,
  fetchMetric,
  setMetricDataStatusPresent,
} from '@/trendData/trendMetric.actions';
import { addScalar, fetchAllScalars, fetchScalar } from '@/trendData/trendScalar.actions';
import { addScalingColumn, getScalingTableItemProps } from '@/trendData/trendScalingColumn.actions';
import {
  addSeries,
  clearPreviewSeries,
  clearSeriesData,
  fetchPairedSignal,
  fetchPairedSignalWithinCondition,
  recordPreviewSeries,
  recordSeriesData,
  recordSignalSegmentsData,
  removeAllCapsuleSeries,
  removeSeriesFromCapsules,
  setSeriesDataStatusHiddenFromTrend,
  setSeriesDataStatusLoading,
  setSeriesDataStatusNotRequired,
  setSeriesProperties,
} from '@/trendData/trendSeries.actions';
import { getCapsuleSeries, getLongestCapsuleSeriesDuration } from '@/trendData/trendSeries.selectors';
import { buildSignalSegmentsFormula } from '@/trendData/trendSeries.utilities';
import { extractStatsFromTable } from '@/trendData/trendStatistics.utilities';
import { addTable, fetchAllFftTables, fetchAllTables, fetchTableData } from '@/trendData/trendTable.actions';
import { computeStaleness, getTemplateProps } from '@/trendData/trendTemplate.actions';
import { removeGaps, updateLaneDisplay } from '@/trendData/yAxis.actions';
import { fetchCapsulesPaneTable } from '@/utilities/capsulesPaneTable.utilities';
import { DUMMY_ITEM_PREFIX, SPIKECATCHER_PER_PIXEL } from '@/utilities/formula.constants';
import {
  computeCapsulesWithLimit,
  computeSamples,
  computeTable,
  getBuildAdditionalFormula,
  getDependencies,
  getPropertyAndStatisticsColumns,
} from '@/utilities/formula.utilities';
import { isCanceled } from '@/utilities/http.utilities';
import { logError, logWarn } from '@/utilities/logger';
import { formatMessage } from '@/utilities/logger.utilities';
import { formatNumber } from '@/utilities/numberHelper.utilities';
import { handleForbidden, isForbidden, isItemRedacted } from '@/utilities/redaction.utilities';
import { notifyCancellation } from '@/utilities/screenshot.utilities';
import { errorToast, infoToast, successToast, warnToast } from '@/utilities/toast.utilities';
import { groupedNonCapsuleSeries, isHidden } from '@/utilities/trendChartItemsHelper.utilities';
import {
  getMSPerPixelWidth,
  getToolType,
  isApplePlatform,
  isCapsuleFullyVisible,
  isInWorkbookRouteAndWorkbookLoaded,
  isStringSeries,
  isTimestampVisible,
  isTrendable,
} from '@/utilities/utilities';
import { isPresentationWorkbookMode } from '@/utilities/workbookMode.utilities';
import { addRecentlyAccessed } from '@/workbook/workbook.actions';
import { WORKSHEET_VIEW } from '@/worksheet/worksheet.constants';
import { sqContextApi } from '@seeqdev/api/endpoint/ContextApi';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { ErrorTypeEnum } from '@seeqdev/api/model/FormulaErrorOutputV1';
import { ProcessTypeEnum } from '@seeqdev/api/model/ThresholdMetricOutputV1';
import { SeeqNames, TREND_COLORS } from '@seeqdev/constants';
import type { PushOption } from '@/core/flux.service';
import type { SelectedSearchItem } from '@/search/search.types';
import type { FrontendDuration } from '@/services/systemConfiguration.types';
import type { RangeExport } from '@/trendData/duration.types';
import type { CompareViewColorMode, PropertyColumn, SwapPairs } from '@/trendData/trendData.constants';
import type {
  BaseItem,
  CapsuleCalculatedChartItem,
  CapsuleItem,
  CapsuleToAdd,
  CustomPropertyValuesAndUOMs,
  SeriesItem,
} from '@/trendData/trendData.types';
import type { AddDataPayload } from '@/trendData/trendSeries.store';
import type { CapsuleFormulaTableRow, ParametersMap, TableSortParams } from '@/utilities/formula.constants';
import type { FormatOptions } from '@/utilities/numberHelper.utilities';
import type { AnyProperty, WithProperty } from '@/utilities.types';
import type { SummaryTypeEnum } from '@seeqdev/api/model/ContentInputV1';
import type { TableColumnOutputV1 } from '@seeqdev/api/model/TableColumnOutputV1';
import type { ItemOutputV1, ItemPreviewV1, ThresholdMetricOutputV1 } from '@seeqdev/api/model/models';

recordChartWidth(process.env.NODE_ENV === 'test' ? 1 : computeChartWidth(window.innerWidth));
let debouncedSetCapsuleTimeOffsets: ReturnType<typeof _.debounce>;
let debouncedFetchAllTimeseries: ReturnType<typeof _.debounce>;
const DATASOURCE_NAME = 'datasourceName';
const PIXELS_PER_BREAK = 0;
const previewSeriesCancellationGroup = 'previewChartSeries';
const fetchConditionsDebounced = _.debounce(() => {
  exposedForTesting.fetchAllTimebarCapsules();
  exposedForTesting.fetchTableAndChartCapsules();
  fetchPlot();
}, DEBOUNCE.SHORT);
const fetchHiddenDebounced = _.debounce(() => exposedForTesting.fetchHiddenTrendData(), DEBOUNCE.SHORT);
const fetchCapsuleTimeDebounced = _.debounce(() => {
  exposedForTesting.addCapsuleTimeSegments(true);
  exposedForTesting.createStitchDetails();
}, DEBOUNCE.SHORT);
const fetchAllStatisticsDebounced = _.debounce(() => exposedForTesting.fetchAllStatistics(true), DEBOUNCE.SHORT);

const debouncedStatisticsFetchers: Record<string, (item: any) => void> = {};
function fetchStatisticsDebounced(item: any) {
  if (!debouncedStatisticsFetchers[item.id]) {
    debouncedStatisticsFetchers[item.id] = _.debounce((item: any) => {
      exposedForTesting.fetchStatistics(item);
      delete debouncedStatisticsFetchers[item.id];
    }, DEBOUNCE.SHORT);
  }
  debouncedStatisticsFetchers[item.id](item);
}

type ContextConditionsMap = Record<string, string>;

/**
 * Generates series for preview purposes.
 *
 * @param formula - the formula to use to generate the series
 * @param {object} parameters - the parameters to use to resolve the formula.
 * @param seriesId - the id of the series or undefined if creating a new search
 * @param color - the color to display the resulting capsules in formatted in hex code (e.g. #CCCCCC)
 * @param pushOption - One of the PUSH constants, applied when the preview is recorded
 * @returns {Promise} that resolves once the results have been added to the chart
 */
export function generatePreviewSeries(
  formula: string,
  parameters: ParametersMap,
  seriesId: string,
  color: string,
  pushOption?: PushOption,
) {
  if (!formula) {
    return Promise.resolve();
  }
  let series = sqTrendSeriesStore.findItem(seriesId);
  const numPixels = Math.min(getStoredChartWidth(), getMaxSeriesPixels());

  // If loading for the first time we won't have a chartWidth, so no need to fetch data that will be overwritten as
  // soon as the chart is instantiated.
  if (!_.isNumber(getStoredChartWidth())) {
    return Promise.resolve();
  }

  if (_.isUndefined(series) && _.startsWith(seriesId, PREVIEW_ID)) {
    series = sqTrendSeriesStore.previewChartItem;
  }

  const laneWidth = `${getMSPerPixelWidth(getDurationDisplayRange().duration.asMilliseconds(), numPixels)}ms`;
  const downSampleFormula = sqTrendStore.buildDownsampleFormula(laneWidth, seriesId);
  return cancelGroup(previewSeriesCancellationGroup)
    .then(() =>
      computeSamples({
        formula: `${formula}${sqTrendStore.buildSummarizeFormula(seriesId)}${downSampleFormula}`,
        parameters,
        range: getDurationDisplayRange(),
        limit: numPixels * SPIKECATCHER_PER_PIXEL,
        cancellationGroup: previewSeriesCancellationGroup,
      }),
    )
    .then((result) => {
      const parametersForDispatch = _.mapValues(parameters, (param) => (_.isNumber(param) ? `${param}` : param));
      recordPreviewSeries({
        formula,
        parameters: parametersForDispatch,
        id: seriesId,
        color,
        samples: result.samples,
        valueUnitOfMeasure: result.valueUnitOfMeasure,
        interpolationMethod: result.interpolationMethod,
        lane: series && series.lane ? series.lane : sqTrendStore.getNextLane(),
        alignment: series && series.axisAlign ? series.axisAlign : sqTrendStore.nextAlignment,
        pushOption,
      });
    })
    .catch((e) => {
      const id = _.get(series, 'id', PREVIEW_ID);
      recordPreviewSeries({ pushOption });
      exposedForTesting.catchItemDataFailure(id, previewSeriesCancellationGroup, e);
    });
}

/**
 * Dispatches the TREND_SET_SUMMARY call to the store, updating the dataSummary in the store with the
 * given payload
 * @param summary - The new summary values
 * @param refetch - Trigger refetch time series or not
 */
export function setTrendSummary(
  summary: {
    type: SummaryTypeEnum;
    value: number;
    isSlider: boolean;
    discreteUnits: FrontendDuration;
  },
  refetch = true,
) {
  flux.dispatch('TREND_SET_SUMMARY', summary);

  if (refetch) {
    if (sqTrendStore.view === TREND_VIEWS.CAPSULE) {
      exposedForTesting.addCapsuleTimeSegments(true);
    } else {
      debouncedFetchAllTimeseries = lazyDebounceOf(exposedForTesting.fetchAllTimeseries, debouncedFetchAllTimeseries);
      debouncedFetchAllTimeseries();
    }
  }
}

/**
 * Sets the status for all non-redacted items displayed in the detailsPanel to "not required". This is useful when
 * requests are forcefully cancelled (without the user requesting the cancellation explicitly) and avoiding issues
 * with the loading indicator.
 */
export function setItemStatusNotRequired() {
  _.forEach(
    getAllItems({
      excludeDataStatus: [ITEM_DATA_STATUS.REDACTED],
    }),
    (item) => {
      setDataStatusNotRequiredInTrendStores(item.id);
    },
  );
}

/**
 * Cancels the preview series
 */
export function cancelPreviewSeries() {
  return cancelGroup(previewSeriesCancellationGroup);
}

/**
 * After darkMode flag is flipped makes sure color for the uncertain capsule filler is refreshed.
 * **/
export function refreshUncertainItemsForDarkMode(): void {
  refreshRuntimeUncertainItems();
}

/**
 * Removes the preview Series from the trend.
 *
 * @param [option] - One of the PUSH constants
 */
export function removePreviewSeries(...option: [PushOption?]) {
  clearPreviewSeries(...option);
}

/**
 * Sets one or more properties to be applied to a panel
 *
 * @param panel - The name of the panel. Must be one of TREND_PANELS
 * @param props - Keys and values to apply to the panel
 */
export function setPanelProps(panel: string, props: object) {
  if (!_.includes(_.values(TREND_PANELS), panel)) {
    throw new TypeError(`${panel} is not a valid panel`);
  }

  flux.dispatch('TREND_SET_PANEL_PROPS', { panel, props });
  if (panel === TREND_PANELS.BOTTOM) {
    setChartWidth();
  }
}

/**
 * Sets the offsets for capsule time
 *
 * @param lower - The duration of the lower offset
 * @param upper - The duration of the upper offset
 * @param {String} [option] - One of the WORKSTEP_PUSH constants
 */
export function setTrendCapsuleTimeOffsets(lower: number, upper: number, option?: PushOption) {
  flux.dispatch('TREND_SET_CAPSULE_TIME_OFFSETS', { lower, upper }, option);

  debouncedSetCapsuleTimeOffsets = lazyDebounceOf(onSetCapsuleTimeOffsets, debouncedSetCapsuleTimeOffsets);
  debouncedSetCapsuleTimeOffsets();

  function onSetCapsuleTimeOffsets() {
    exposedForTesting.addCapsuleTimeSegments(true);
  }
}

/**
 * Resets the capsule time offsets to their defaults, as though the user has not changed the x-axis.
 */
export function resetTrendCapsuleTimeOffsets() {
  exposedForTesting.setTrendCapsuleTimeOffsets(0, 0);
}

/**
 * Sets the selected region on the chart and updates statistics since they are tied to the selected region.
 *
 * @param min - A timestamp for the minimum side of the range.
 * @param max - A timestamp for the maximum side of the range.
 * @param {String} [option] - One of the WORKSTEP_PUSH constants
 */
export function setTrendSelectedRegion(min: number, max: number, option?: PushOption) {
  flux.dispatch('TREND_SET_SELECTED_REGION', { min, max }, option);
  sqProfileSearchStore.actions.setSelectedRegion({ min, max });
  exposedForTesting.fetchAllStatistics(sqTrendStore.view !== TREND_VIEWS.CAPSULE);
}

/**
 * Removes the selected region.
 *
 * @param {String} [option] - One of the WORKSTEP_PUSH constants
 */
export function removeTrendSelectedRegion(option?: PushOption) {
  exposedForTesting.setTrendSelectedRegion(0, 0, option);
}

/**
 * Register options to display when selected region plus icon is clicked
 *
 * @param identifier - The plugin identifier.
 * @param options - The options to display on selected region plus icon.
 */
export function addTrendSelectedRegionOptions(identifier: string, options: string[]) {
  flux.dispatch('TREND_ADD_SELECTED_REGION_OPTIONS', { [identifier]: options });
}

/**
 * Register options to display when selected region plus icon is clicked
 *
 * @param identifier - The plugin identifier.
 * @param options - The options to display on selected region plus icon.
 */
export function removeTrendSelectedRegionOptions() {
  flux.dispatch('TREND_REMOVE_SELECTED_REGION_OPTIONS', {});
}

/**
 * Zooms to the selected region.
 *
 * @param [keepRegion=false] - If true, does not remove the selected region
 * @param [skipInvestigateCheck=false] - If true, prevents investigateRange from being automatically expanded
 */
export function zoomTrendToSelectedRegion(keepRegion = false, skipInvestigateCheck = false) {
  if (!sqTrendStore.isRegionSelected) {
    return;
  }

  const min = sqTrendStore.selectedRegion.min;
  const max = sqTrendStore.selectedRegion.max;
  if (sqTrendStore.view === TREND_VIEWS.CAPSULE) {
    exposedForTesting.setTrendCapsuleTimeOffsets(0 + min, max - getLongestCapsuleSeriesDuration());
  } else {
    displayRange.updateTimes(min, max, false, skipInvestigateCheck);
  }

  if (!keepRegion) {
    exposedForTesting.removeTrendSelectedRegion();
  }
}

export function updateCapsuleGrouping() {
  if (sqTrendStore.isTrendViewCapsuleTime()) {
    exposedForTesting.addCapsuleTimeSegments();
  } else if (sqTrendStore.isTrendViewChainView()) {
    exposedForTesting.createStitchDetails();
  }
}

/**
 * Changes the view mode. If moving to a new view it prepares for the new chart.  In capsule time it then converts
 * all capsules into timeseries items, because capsule time is a chart displaying the timeseries for a capsule's
 * start and end times.
 *
 * @param view - Set the new view: TREND_VIEWS.CALENDAR, TREND_VIEWS.CHAIN, or TREND_VIEWS.CAPSULE
 */
export function setTrendView(view: string) {
  const previousView = sqTrendStore.view;
  const requestsToCancel =
    previousView === TREND_VIEWS.CAPSULE ? getCapsuleSeries() : sqTrendSeriesStore.nonCapsuleSeries;

  _.forEach(requestsToCancel, (series: any) => {
    setSeriesDataStatusNotRequired(series.id);
    return cancelGroup(`fetchTimeseries${series.id}`, true);
  });

  flux.dispatch('TREND_SET_VIEW', { view });

  if (view === TREND_VIEWS.CAPSULE) {
    exposedForTesting.addCapsuleTimeSegments(true);
  } else if (view === TREND_VIEWS.CHAIN) {
    recomputeRuntimeChartItems();

    let capsulePromise;
    // re-fetch the chart capsules if we're coming from capsule time as those could have changed (CRAB-20414)
    if (previousView === TREND_VIEWS.CAPSULE) {
      capsulePromise = exposedForTesting.fetchChartCapsules();
    } else {
      capsulePromise = Promise.resolve();
    }

    capsulePromise.then(() => {
      exposedForTesting.createStitchDetails();
    });
  } else {
    exposedForTesting.fetchAllTimeseries();
    exposedForTesting.fetchChartCapsules();
  }

  // Remove the selected region when navigating to or away from capsule view
  if (
    (previousView !== TREND_VIEWS.CAPSULE && view === TREND_VIEWS.CAPSULE) ||
    (previousView === TREND_VIEWS.CAPSULE && view !== TREND_VIEWS.CAPSULE)
  ) {
    exposedForTesting.removeTrendSelectedRegion();
  }

  // The behavior of fetchTableAndChartCapsules changes slightly in dimming mode
  if (
    (previousView !== TREND_VIEWS.CALENDAR && view === TREND_VIEWS.CALENDAR) ||
    (previousView === TREND_VIEWS.CALENDAR && view !== TREND_VIEWS.CALENDAR)
  ) {
    // This mirrors the logic in `fetchTableAndChartCapsules` for getting the conditions to fetch
    if (
      _.chain(
        getAllItems({
          excludeDataStatus: [ITEM_DATA_STATUS.REDACTED, ITEM_DATA_STATUS.FAILURE, ITEM_DATA_STATUS.CANCELED],
          workingSelection: true,
          excludeEditingCondition: true,
          itemTypes: [ITEM_TYPES.CONDITION],
        }),
      )
        .some((item) => isHidden({ item }))
        .value()
    ) {
      // If some items in the working selection are hidden we need to re-fetch the capsule table
      // because the conditionIds that we would use to build the table have changed with the view
      exposedForTesting.fetchTableAndChartCapsules();
    }
  }

  if (view !== TREND_VIEWS.CHAIN) {
    writeRuntimeStitchBreaks([]);
    sqCapsuleRuntimeStore.actions.setStitchTimes([]);
  }

  // This is more of a sanity check that all the displayed data is shown; if all the data is already shown this
  // will be a no-op.
  exposedForTesting.fetchHiddenTrendData();
}

const manageSignalSegmentsLoad = ({
  interest,
  capsules,
  isFetchRequired,
}: {
  interest: Partial<SeriesItem>;
  capsules: CapsuleItem[];
  isFetchRequired: boolean;
}) => {
  // We only want to run the code below if the capsule will appear on the trend
  if (isHidden({ item: interest }) || !interest.id) {
    return;
  }

  const capsulesToLoad: CapsuleItem[] = [];
  _.forEach(capsules, (capsule) => {
    const existingCapsuleSeries = _.find(getCapsuleSeries(), {
      capsuleId: capsule.id,
      interestId: interest.id,
    });
    if (existingCapsuleSeries) {
      const { properties } = capsule;
      let compareProperties = {};
      if (!_.isEmpty(properties)) {
        const { column, compareBy } = existingCapsuleSeries;
        compareProperties = {
          column: properties[sqTrendStore.separateByProperty!] || column,
          colorBy: properties[sqTrendStore.colorByProperty!] || compareBy,
        };
      }
      setSeriesProperties(
        {
          id: existingCapsuleSeries.id,
          dirty: false,
          ...compareProperties,
        },
        PUSH_IGNORE,
      );
    } else {
      flux.dispatch('TREND_ADD_SERIES_FROM_CAPSULE', { capsule, interest }, PUSH_IGNORE);
      capsulesToLoad.push(capsule);
    }
  });

  if (isFetchRequired) {
    return exposedForTesting.fetchSignalSegments(interest.id, capsules);
  }

  if (capsulesToLoad.length > 0) {
    return exposedForTesting.fetchSignalSegments(interest.id, capsulesToLoad);
  }
};

/**
 * Adds all capsule time segments for the given time series and makes each series an interest of its respective
 * capsule. Defaults to adding all capsules for all time series. Existing capsule series are simply updated with
 * new data, new ones are added, and no longer displayed ones are removed. Simply removing all the capsule series
 * would cause an undesirable flicker when auto-updated is enabled.
 *
 * @param [isFetchRequired] - True if fetching the data is required for existing capsule series
 * @param {Array<Item>} [seriesToUpdate] - The array of time series for which capsule time segments will be added.
 * If not provided, defaults to all capsules in the capsule store
 * @returns {Promise} Resolves when all timeseries have been fetched
 */
export function addCapsuleTimeSegments(
  isFetchRequired = false,
  seriesToUpdate: { selected?: boolean; id: string }[] = [],
) {
  const MAX_CAPSULE_TIME_ITEMS = sqTrendStore.getMaxCapsuleTimeItems();

  if (!sqTrendStore.isTrendViewCapsuleTime() || sqTrendStore.capsulePanelIsLoading) {
    return;
  }

  // The store returns items that are Partial<SeriesItem> with at least id, selected, isChildOf, interestId, capsuleId
  type SeriesLike = Partial<SeriesItem>;
  let itemsOfInterest: SeriesLike[];
  let existingCapsuleSeries: SeriesLike[];
  if (_.isEmpty(seriesToUpdate)) {
    itemsOfInterest =
      sqTrendStore.hideUnselectedItems && _.some(sqTrendSeriesStore.nonCapsuleSeries, ['selected', true])
        ? _.filter(sqTrendSeriesStore.nonCapsuleSeries, ['selected', true])
        : sqTrendSeriesStore.nonCapsuleSeries;
    existingCapsuleSeries = getCapsuleSeries();
  } else {
    itemsOfInterest =
      sqTrendStore.hideUnselectedItems && _.some(seriesToUpdate, ['selected', true])
        ? _.filter(seriesToUpdate, ['selected', true])
        : seriesToUpdate;
    existingCapsuleSeries = _.filter(getCapsuleSeries(), (capsuleSeries) =>
      _.find(itemsOfInterest, { id: capsuleSeries.interestId }),
    );
  }

  _.forEach(existingCapsuleSeries, (capsuleSeries) => {
    setSeriesProperties({ id: capsuleSeries.id!, dirty: true }, PUSH_IGNORE);
  });

  let allCapsuleSeriesLoads: Promise<any[]>;
  let isCapsuleTimeLimited = false;
  let visibleItemsCount = 0;
  let visibleCapsulesCount = 0;

  // If capsule group mode is enabled then only signals that are paired to a condition are shown during the
  // conditions capsules. This requires filtering of the capsules by condition and then explicit loading of only
  // the grouped signals.
  if (sqWorksheetStore.capsuleGroupMode) {
    let seriesCapsulePairList = _.chain(_.keys(sqWorksheetStore.conditionToSeriesGrouping))
      .flatMap((conditionId) => {
        // find the capsules that belong to a given condition
        const condition = { isChildOf: conditionId, notFullyVisible: false };
        _.assign(
          condition,
          sqTrendStore.hideUnselectedItems && _.some(getCapsuleItems(), { selected: true }) ? { selected: true } : {},
        );
        return _.filter(getCapsuleItems(), condition);
      })
      .flatMap((capsule: any) =>
        _.chain(itemsOfInterest)
          .filter((series) =>
            // ensure only linked signal's segments are loaded.
            _.includes(sqWorksheetStore.conditionToSeriesGrouping[capsule.isChildOf], series.id),
          )
          .compact()
          .reject('isChildOf')
          .map((interest) => {
            return { interest, capsule };
          })
          .value(),
      )
      .value();

    if (seriesCapsulePairList.length > MAX_CAPSULE_TIME_ITEMS) {
      seriesCapsulePairList = seriesCapsulePairList.slice(0, MAX_CAPSULE_TIME_ITEMS);
      isCapsuleTimeLimited = true;
      visibleItemsCount = _.uniqBy(seriesCapsulePairList, 'interest').length;
      visibleCapsulesCount = _.uniqBy(seriesCapsulePairList, 'capsule').length;
    }
    const capsulesBySeriesId = _.reduce(
      seriesCapsulePairList,
      (result, value) => {
        const signalId = value.interest.id;
        if (signalId) {
          (result[signalId] || (result[signalId] = [])).push(value.capsule);
        }
        return result;
      },
      {} as Record<string, CapsuleItem[]>,
    );

    allCapsuleSeriesLoads = _.chain(capsulesBySeriesId)
      .flatMap((capsules, interestId) => {
        const interest = sqTrendSeriesStore.findItem(interestId);

        return manageSignalSegmentsLoad({ interest, capsules, isFetchRequired });
      })
      .thru((promises) => Promise.all(promises))
      .value();
  } else {
    const capsules =
      sqTrendStore.hideUnselectedItems && _.some(getCapsuleItems(), ['selected', true])
        ? _.filter(getCapsuleItems(), ['selected', true])
        : getCapsuleItems();

    const visibleCapsules = _.reject(capsules, (capsule) => !isTimestampVisible(capsule.startTime));
    const allVisibleItems = _.chain(itemsOfInterest)
      .reject((item) => isHidden({ item }))
      .reject('isChildOf')
      .value();
    // low probability to trend more than MAX_CAPSULE_TIME_ITEMS items, but if this happens, take
    // MAX_CAPSULE_TIME_ITEMS items and show one capsule for each of them
    const displayableItems = allVisibleItems.slice(0, Math.min(MAX_CAPSULE_TIME_ITEMS, allVisibleItems.length));
    const maxSegmentsPerItem = MAX_CAPSULE_TIME_ITEMS / displayableItems.length;

    const segmentsToLoad = visibleCapsules.slice(0, Math.min(maxSegmentsPerItem, visibleCapsules.length));
    allCapsuleSeriesLoads = _.chain(displayableItems)
      .flatMap((interest) => manageSignalSegmentsLoad({ interest, capsules: segmentsToLoad, isFetchRequired }))
      .thru((promises) => Promise.all(promises))
      .value();

    visibleItemsCount = allVisibleItems.length;
    visibleCapsulesCount = visibleCapsules.length;
    isCapsuleTimeLimited = visibleItemsCount * visibleCapsulesCount > MAX_CAPSULE_TIME_ITEMS;
  }

  flux.dispatch(
    'TREND_SET_CAPSULE_TIME_LIMITED',
    {
      enabled: isCapsuleTimeLimited,
      itemsCount: visibleItemsCount,
      capsulesCount: visibleCapsulesCount,
    },
    PUSH_IGNORE,
  );

  return allCapsuleSeriesLoads.finally(() => {
    const seriesFromCapsules = _.chain(getCapsuleSeries())
      .filter('dirty')
      .map((capsuleSeries) => ({
        capsuleId: capsuleSeries.capsuleId,
        interestId: capsuleSeries.interestId,
      }))
      .value();
    if (!_.isEmpty(seriesFromCapsules)) {
      removeSeriesFromCapsules({ seriesFromCapsules }, PUSH_IGNORE);
    }
    exposedForTesting.setCapsuleTimeColorMode();
    updateLaneDisplay();
  });
}

/**
 * Removes all unselected capsule for each series (also removes each capsule as an interest of the capsule).
 */
export function removeUnselectedSeriesFromCapsules() {
  const removeUnselectedConditions =
    !sqTrendStore.isTrendViewCapsuleTime() || _.some(getCapsuleItems(), ['selected', true]);
  const removeUnselectedSeries =
    !sqTrendStore.isTrendViewCapsuleTime() || _.some(sqTrendSeriesStore.nonCapsuleSeries, ['selected', true]);
  const remainingSeriesFromCapsules: Array<{ capsuleId: string; interestId: string }> = [];
  const seriesFromCapsules: Array<{ capsuleId: string; interestId: string }> = [];
  _.forEach(getCapsuleItems(), function (capsule) {
    _.forEach(sqTrendSeriesStore.nonCapsuleSeries, function (interest) {
      if ((!capsule.selected && removeUnselectedConditions) || (!interest.selected && removeUnselectedSeries)) {
        seriesFromCapsules.push({
          capsuleId: capsule.id,
          interestId: interest.id,
        });
      } else {
        remainingSeriesFromCapsules.push({
          capsuleId: capsule.id,
          interestId: interest.id,
        });
      }
    });
  });
  if (!_.isEmpty(seriesFromCapsules)) {
    removeSeriesFromCapsules({ seriesFromCapsules });
  }

  const visibleItemsCount = _.uniqBy(remainingSeriesFromCapsules, 'interestId').length;
  const visibleCapsulesCount = _.uniqBy(remainingSeriesFromCapsules, 'capsuleId').length;
  const isCapsuleTimeLimited = visibleItemsCount * visibleCapsulesCount > sqTrendStore.getMaxCapsuleTimeItems();

  flux.dispatch(
    'TREND_SET_CAPSULE_TIME_LIMITED',
    {
      enabled: isCapsuleTimeLimited,
      itemsCount: visibleItemsCount,
      capsulesCount: visibleCapsulesCount,
    },
    PUSH_IGNORE,
  );
}

/**
 * Sets the color mode for the lines in each lane of capsule time. There are four different modes:
 * - Signal: every line gets its color from its signal, regardless of its condition
 * - Rainbow: every line gets a unique color, except that sort-order acts as a proxy for grouping colors
 * - SignalGradient: the base color is that of the signal and then a gradient is computed from a lighter version
 * of the color. The same grouping rules apply as in Rainbow.
 * - ConditionGradient: the base color is that of the condition and then a gradient is computed from a lighter version
 * of the color. The same grouping rules apply as in Rainbow.
 *
 * @param mode - The color mode
 */
export function setCapsuleTimeColorMode(mode: CapsuleTimeColorMode = sqTrendStore.capsuleTimeColorMode) {
  if (sqTrendStore.view !== TREND_VIEWS.CAPSULE) {
    return;
  }

  const pushMode = mode === sqTrendStore.capsuleTimeColorMode ? PUSH_IGNORE : undefined;
  flux.dispatch('TREND_SET_CAPSULE_TIME_COLOR_MODE', { mode }, pushMode);
  const sortBy = sqTrendStore.getPanelSort(TREND_PANELS.CAPSULES).sortBy;
  const capsules = _.reject(getCapsuleItems(), (item) => item.notFullyVisible) as any[];
  const requiredColorsCount = _.uniqBy(capsules, (capsule) => _.get(capsule, sortBy)).length;
  const itemsForGradients =
    {
      [CapsuleTimeColorMode.ConditionGradient]: sqTrendConditionStore.items,
      [CapsuleTimeColorMode.SignalGradient]: sqTrendSeriesStore.nonCapsuleSeries,
      [CapsuleTimeColorMode.Rainbow]: [],
      [CapsuleTimeColorMode.Signal]: [],
    }[mode] || [];
  const colorGradients = _.transform(
    itemsForGradients as any[],
    (memo, item) => {
      memo[item.id] =
        requiredColorsCount < 2
          ? [item.color]
          : _.chain(computeLightestColor(item.color, 0.2))
              .thru((lightestColor) => tinygradient([lightestColor, item.color]).rgb(requiredColorsCount))
              .map((color) => color.toString('rgb'))
              .value();
    },
    {} as Record<string, string[]>,
  );

  let previousGroupByValue: unknown;
  const previousColors: Record<string, [string, string?]> = {};
  let index = 0;
  _.forEach(capsules, (capsule) => {
    let capsuleValue = _.get(capsule, sortBy);
    if (_.startsWith(sortBy, 'statistics')) {
      capsuleValue = formatNumber(capsuleValue, capsule.formatOptions);
    }

    const isGrouped =
      !_.isUndefined(previousGroupByValue) &&
      previousGroupByValue === capsuleValue &&
      mode !== CapsuleTimeColorMode.Signal;

    _.chain(sqTrendSeriesStore.findChildren(capsule.id))
      .filter({ childType: ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE })
      .forEach((signal) => {
        const [color, childColor] = isGrouped ? previousColors[signal.isChildOf] : computeColors(signal);
        previousColors[signal.isChildOf] = [color, childColor];

        exposedForTesting.setTrendItemColor(signal.id, color, pushMode);
        exposedForTesting.setTrendItemProps(capsule.id, { childColor: childColor || color }, pushMode);
      })
      .value();

    if (!isGrouped) {
      index++;
    }
    previousGroupByValue = capsuleValue;

    function computeColors(signal: any): [string, string?] {
      switch (mode) {
        case CapsuleTimeColorMode.Rainbow:
          return [TREND_COLORS[index % TREND_COLORS.length]];
        case CapsuleTimeColorMode.ConditionGradient: {
          const conditionGradient = colorGradients[capsule.isChildOf];
          return [conditionGradient[index % conditionGradient.length]];
        }
        case CapsuleTimeColorMode.SignalGradient: {
          const signalGradient = colorGradients[signal.isChildOf];
          const color = signalGradient[index % signalGradient.length];
          return [color, tinycolor(color).greyscale().toString('rgb')];
        }
        case CapsuleTimeColorMode.Signal:
          return [sqTrendSeriesStore.findItem(signal.isChildOf).color];
        default:
          throw new TypeError(`Unknown capsule time color mode: ${mode}`);
      }
    }
  });
}

export function setCompareViewColorMode(mode: CompareViewColorMode) {
  flux.dispatch('TREND_SET_COMPARE_VIEW_COLOR_MODE', { mode });
}

/**
 * Creates and sets the stitch details
 *
 * @returns {Promise} Resolves when data for stitches is fetched.
 */
export function createStitchDetails() {
  if (sqTrendStore.view !== TREND_VIEWS.CHAIN) {
    return Promise.resolve();
  }

  const stitchNumPixels = Math.min(getStoredChartWidth(), getMaxSeriesPixels());
  writeRuntimeStitchDetails(stitchNumPixels);

  if (sqCapsuleRuntimeStore.getState().stitchDetailsSet) {
    return exposedForTesting.fetchAllTimeseries();
  } else {
    return Promise.resolve();
  }
}

/**
 * Fetches statistics for all signals and conditions in the details pane.
 *
 * @param fetchSeries - True if the stats for series should be fetched in addition to conditions stats
 */
export function fetchAllStatistics(fetchSeries = false) {
  const itemTypes = fetchSeries ? [ITEM_TYPES.SERIES, ITEM_TYPES.CONDITION] : [ITEM_TYPES.CONDITION];
  return _.chain(
    getAllItems({
      itemTypes,
      itemChildrenTypes: [ITEM_CHILDREN_TYPES.METRIC_DISPLAY],
      workingSelection: !!sqTrendStore.hideUnselectedItems,
      excludeDummyItems: true,
    }),
  )
    .map(_.unary(exposedForTesting.fetchStatistics))
    .thru((p) => Promise.all(p))
    .value();
}

/**
 * Updates the statistics for conditions or signals. This entails figuring out the correct time range for which to
 * request stats. It is either the selected region, if present, or the display range. A formula is built that
 * returns a statistic that corresponds to each enabled stat column in the details pane.
 *
 * @param item - The condition or signal
 * @return Resolves when all the statistics finish computing
 */
export function fetchStatistics(item: {
  id: string;
  itemType: string;
  childType?: string;
  isChildOf?: string;
  valueUnitOfMeasure?: string;
  sourceValueUnitOfMeasure?: string;
  signalMetadata?: { valueUnitOfMeasure?: string };
}) {
  const stats =
    item.itemType === ITEM_TYPES.SCALAR
      ? []
      : _.chain(item.itemType === ITEM_TYPES.SERIES ? TREND_SIGNAL_STATS : TREND_CONDITION_STATS)
          .filter((stat: any) => sqTrendStore.isColumnEnabled(TREND_PANELS.SERIES, stat.key))
          .filter((stat: any) => (isStringSeries(item) ? stat.isStringCompatible : true))
          .value();

  if (
    (item.childType && item.childType !== ITEM_CHILDREN_TYPES.METRIC_DISPLAY) ||
    _.isEmpty(stats) ||
    sqWorksheetStore.view.key !== WORKSHEET_VIEW.TREND ||
    isPresentationWorkbookMode()
  ) {
    return Promise.resolve();
  }

  const viewCapsule = getCapsuleFormula(
    sqTrendStore.isRegionSelected
      ? {
          start: sqTrendStore.selectedRegion.min,
          end: sqTrendStore.selectedRegion.max,
        }
      : getDurationDisplayRange(),
  );
  const enums = _.map(stats, 'stat').join(', ');

  let dispatchItemId = item.id;

  // Compute statistics on the metric's display signal but dispatch the results to the metric item.
  if (item.childType === ITEM_CHILDREN_TYPES.METRIC_DISPLAY && item.isChildOf) {
    const parentMetric = sqTrendMetricStore.findItem(item.isChildOf);
    const metricProcessType = _.get(parentMetric, 'definition.processType');

    if (metricProcessType && metricProcessType !== ProcessTypeEnum.Simple) {
      dispatchItemId = parentMetric.id;
    } else {
      // We can't compute statistics for simple metrics because display signal is a formula function
      // Reset in case the type has changed
      sqItemRuntimeStore.actions.setStatistics(parentMetric.id, {});
      return Promise.resolve();
    }
  }

  const cancellationGroup = `stats${CANCELLATION_GROUP_GUID_SEPARATOR}${item.id}`;
  const formula = `group(${viewCapsule}).toTable('stats').addStatColumn('series', $series, ${enums})`;
  // Reset all enabled statistics for this item before requesting new values
  sqItemRuntimeStore.actions.setStatistics(dispatchItemId, {});
  return cancelGroup(cancellationGroup, true)
    .then(() =>
      computeTable({
        formula,
        parameters: { series: getItemId(item) },
        cancellationGroup,
      }).then((table: { data: any[] }) => {
        // Stat keys are paths like 'statistics.average', so collect into a wrapper and unwrap
        const result: { statistics: Record<string, unknown> } = { statistics: {} };
        _.chain(table.data[0]) // Only ever one row of data
          .drop(2) // First two columns are always start and end time
          .forEach((val, i) => _.set(result, stats[i].key, val))
          .value();
        sqItemRuntimeStore.actions.setStatistics(dispatchItemId, result.statistics);
      }),
    )
    .catch((e) => exposedForTesting.catchItemDataFailure(item.id, cancellationGroup, e));
}

/**
 * Adds an item that comes from the REST API to one of the item stores. Since this is meant to be invoked when the
 * user decides to graph a new item it changes to chain, if the user is in capsule time.
 *
 * This function also fetches statistics (if enabled).
 *
 * @param item - The item to add with a type property that identifies what type of item it is.
 * @param props - additional properties to be set on the item before fetching
 * @param option - One of the WORKSTEP_PUSH constants
 * @param skipDependencies - flags whether to skip property dependencies
 * @throws TypeError if the item type is not recognized.
 * @return A promise that resolves with the item when it is finished fetching.
 */
export function addTrendItem(
  item: any,
  props?: Record<string, unknown>,
  option?: PushOption,
  skipDependencies = false,
): Promise<any> {
  let promise: Promise<unknown>;
  let doUpdateLaneDisplay = true;
  const type = getItemType(item as { itemType?: string; type: string }) || item.type; // Journals don't have an ITEM_TYPE so we compare with the API_TYPES

  if (ITEM_TYPES.SERIES === type) {
    promise = addSeries(item, props, option);
  } else if (ITEM_TYPES.SCALAR === type) {
    promise = addScalar(item, props, option);
  } else if (ITEM_TYPES.CONDITION === type) {
    promise = addCondition(item, props, option);
  } else if (ITEM_TYPES.TABLE === type) {
    doUpdateLaneDisplay = false;
    promise = addTable(item, props, option);
  } else if (ITEM_TYPES.METRIC === type) {
    promise = addMetric(item, props, option);
  } else if (ITEM_TYPES.SCALING_COLUMN === type) {
    promise = addScalingColumn(item, props, option);
  } else if (API_TYPES.JOURNAL === type) {
    return exposedForTesting.addAnnotation(item);
  } else {
    throw new TypeError(`item type "${type}" is not trendable`);
  }

  if (doUpdateLaneDisplay) {
    // Called before data is fetched so that the existing items 'move' out of the way (CRAB-8386).
    updateLaneDisplay();
  }

  // Wait for promise to resolve because we need calculationType to be set on the item
  return promise.then(() => {
    if (!skipDependencies) {
      fetchItemDependencies();
    }

    const addedItem = findItemIn(getTrendStores(), item.id);
    if (addedItem && !addedItem.isChildOf) {
      if (getAllItems({ itemTypes: ITEM_TYPES_WITH_COLUMNS }).some((i) => i.selected)) {
        exposedForTesting.setItemSelected(item, true);
      }

      if (sqWorkbenchStore.stateParams.workbookId && addedItem.itemType !== ITEM_TYPES.SCALING_COLUMN) {
        addRecentlyAccessed(sqWorkbenchStore.stateParams.workbookId, item.id);
      }

      // Lane and asset are special: honor an explicit user-hidden preference (`false`) and only
      // auto-enable when no preference exists yet. Other columns below still force-enable because
      // they are required for the item type to be understandable.
      //
      // Legacy: worksteps saved before explicit `false` was persisted used unset for both "never
      // shown" and "user hid", so the first auto-enable after upgrade may re-show lane/asset once.
      if (
        addedItem.lane &&
        getAllItems({}).length > 1 &&
        !sqTrendStore.hasColumnEnabledPreference(TREND_PANELS.SERIES, 'lane')
      ) {
        exposedForTesting.setColumnEnabled(TREND_PANELS.SERIES, 'lane', true, PUSH_IGNORE);
      }

      if (!_.isEmpty(addedItem.assets) && !sqTrendStore.hasColumnEnabledPreference(TREND_PANELS.SERIES, 'asset')) {
        exposedForTesting.setColumnEnabled(TREND_PANELS.SERIES, 'asset', true, PUSH_IGNORE);
      }

      // Intentionally force-enable (not preference-gated like lane/asset above).
      if (addedItem.columnName && addedItem.rowName && addedItem.tableName) {
        exposedForTesting.setColumnEnabled(TREND_PANELS.SERIES, 'sourceScalingTable', true, PUSH_IGNORE);
      }

      if (isItemFromModelApplication(addedItem)) {
        exposedForTesting.setColumnEnabled(TREND_PANELS.SERIES, 'modelInformationColumn', true, PUSH_IGNORE);
      }

      if (addedItem.nodePath) {
        exposedForTesting.setColumnEnabled(TREND_PANELS.BOTTOM, 'hierarchyPath', true, PUSH_IGNORE);
      }
    }

    return addedItem;
  });
}

export async function batchAddItems(items: any[], isSeparate = true) {
  const extraProps = isSeparate
    ? undefined
    : {
        lane: sqTrendStore.getNextLane(false),
        alignment: sqTrendStore.nextAlignment,
        yAxisType: (_.reject(items, isStringSeries)[0] as Record<string, unknown>)?.yAxisType,
      };

  const addItemPromises = items.map(async (item) => {
    const scalingProps = await getScalingTableItemTrendProps(item as SelectedSearchItem);
    const nodePath = _.map(item.ancestors, 'node').filter((node) => node);
    const hierarchyItemType = item.node?.itemMetadata
      ? API_TYPES_TO_ITEM_TYPES[item.node.itemMetadata.type!]
      : undefined;
    let newItem = { ...item, itemType: hierarchyItemType ?? item.itemType };
    const hierarchyProps = getHierarchyItemTrendProps(newItem as SelectedSearchItem, nodePath);
    const props = scalingProps || hierarchyProps ? { ...scalingProps, ...hierarchyProps } : undefined;
    if (extraProps) {
      newItem = {
        ...newItem,
        ..._.omitBy(
          {
            lane: extraProps.lane,
            axisAlign: isStringSeries(item) ? undefined : extraProps.alignment,
            yAxisType: isStringSeries(item) ? undefined : extraProps.yAxisType,
          },
          _.isUndefined,
        ),
      };
    }
    return props
      ? exposedForTesting.addTrendItem(newItem, props, PUSH_IGNORE, true)
      : exposedForTesting.addTrendItem(newItem, undefined, PUSH_IGNORE, true);
  });
  await Promise.all(addItemPromises);

  if (!isSeparate && _.some(items, isStringSeries) && items.length > 1) {
    infoToast({ messageKey: 'NO_STRING_DATA_Y_AXIS_SHARING' });
  }

  fetchItemDependencies();
  clearSelection();
}

export function fetchItemDependencies() {
  exposedForTesting.fetchTableAndChartCapsules();
  updateDerivedDataTree();
  fetchAnnotations();
  fetchTable();
}

/**
 * Given a specification for an asset swap, calculates the new ID for each item to be swapped
 * using the findSwap API endpoint.
 *
 * @param {SwapPairs} swapPairsForItems - each key is an item ID; each value is the list of swap pairs to be used
 *    for the swap
 * @param {string[]} allItems - an array containing the IDs of all current trend items. Usually the output from
 *    getAllItems from trendDataHelper
 * @param {string[]} itemIdsToSwap - an array containing the IDs out of allItems that should be swapped
 * @param {boolean} suppressWarnings - if true, this function will not raise toast messages to the user for swaps
 *    that don't exist and swaps that are already in the details pane
 * @returns {Object} - dictionary mapping old items IDs to their swapped item IDs
 */
export function getSwaps(
  swapPairsForItems: SwapPairs,
  allItems: ItemPreviewV1[],
  itemIdsToSwap: string[],
  suppressWarnings = false,
) {
  return _.chain(itemIdsToSwap)
    .filter((id: any) => _.has(swapPairsForItems, id))
    .map((id: string) =>
      sqItemsApi
        .findSwap(swapPairsForItems[id], { id })
        .then(({ data }) => ({ id, swappedId: data.id }))
        .catch(function (e) {
          if (e.status === HttpCodes.BAD_REQUEST && !suppressWarnings) {
            warnToast({ httpResponseOrError: e });
          }
        }),
    )
    .thru((promises) => Promise.all(promises as Promise<any>[]))
    .value()
    .then(_.compact)
    .then(function (swaps) {
      // A swap is invalid if it would replace the current item with one that is already in the store;
      // therefore, adding it would cause a duplicate in the Details panel (CRAB-11603).
      const invalidSwaps = _.filter(swaps, (swap) => _.includes(_.map(allItems, 'id'), swap.swappedId));
      if (!_.isEmpty(invalidSwaps)) {
        const invalidSwapOutItemNames = _.map(
          invalidSwaps,
          (swap) => _.find(allItems, ['id', swap.id])?.name ?? '',
        ).join(', ');
        if (!suppressWarnings) {
          warnToast({
            messageKey: 'SWAPS.INVALID_SWAPS_V2',
            messageParams: {
              invalidSwapOutItemNames,
            },
          });
        }

        // Although we don't want to swap items that are already in the details pane, we do need to update the
        // worksheet store's conditionToSeriesGrouping mapping to ensure the groupings point to the correct signal.
        flux.dispatch('WORKSHEET_SWAP_GROUPINGS', {
          swaps: _.zipObject(_.map(invalidSwaps, 'id'), _.map(invalidSwaps, 'swappedId')),
        });
      }
      return _.difference(swaps, invalidSwaps);
    })
    .then((swaps) => _.zipObject(_.map(swaps, 'id'), _.map(swaps, 'swappedId')));
}

/**
 * Swap out one asset for another for all items on the trend. Each item that relies upon signals in the out asset
 * will be swapped for a corresponding version that uses the in asset.
 *
 * @param swapPairsForItems - each key is an item ID; each value is the list of
 * swap pairs to be used for the swap
 * @param [skipFetch] - Skip fetching data after swap
 *
 * @return {Promise} Resolves when all assets have been swapped and data refreshed
 */
export async function swapAssets(swapPairsForItems: SwapPairs, skipFetch = false) {
  const allItems = getAllItems({});
  const itemIdsToSwap = _.chain(allItems).uniqBy('id').map('id').value();

  const swaps = await getSwaps(swapPairsForItems, allItems, itemIdsToSwap);
  if (_.isEmpty(swaps)) {
    return null;
  }
  _.forEach(swaps, (swappedId, id) => {
    exposedForTesting.removeChildren(id);
    // Remove other signals from the chart so that old data and new data is not mixed while loading
    clearSeriesData(id);
  });
  closeInvestigationTool();

  swapItemsInTrendStores({ swaps });
  sqScorecardStore.actions.swapItems(swaps);
  successToast({
    messageKey: 'SWAPS.SUCCESS_V2',
    messageParams: {
      successfulSwapsCount: _.size(swaps),
      allItemsCount: allItems.length,
    },
  });
  return skipFetch ? null : exposedForTesting.fetchAllItems();
}

/**
 * Removes an item and, optionally, its children from the chart. When the removed item is a series or capsule, if
 * there are children for the item, remove them too.
 *
 * @param {Object} item - The item to remove.
 * @param {Boolean} [keepChildren] - True to prevent children of the item from being removed when the item is
 *   removed. (e.g. When you delete a series, don't delete the capsules that are interested in that series)
 */
export function removeTrendItem(item: { id: string }, keepChildren = false) {
  exposedForTesting.removeItems([item], undefined, keepChildren);
}

/**
 * Removes an array of items and, optionally, their children from the chart. When the removed item is a series or
 * capsule, if there are children for the item, remove them too.
 *
 * @param {Object[]} items - The  array of items to remove.
 * @param {Boolean} [keepChildren] - True to prevent children of the item from being removed when the item is
 *   removed. (e.g. When you delete a series, don't delete the capsules that are interested in that series)
 * @param {String} [option] - One of the WORKSTEP_PUSH constants
 */
export async function removeItems(items: any[], option?: PushOption, keepChildren = false) {
  // Short-circuit and avoid the work if not removing anything
  if (!items.length) {
    return;
  }

  items = _.flatMap(items, (item) =>
    !keepChildren || item.itemType === ITEM_TYPES.METRIC ? [item].concat(findAncestors(item)) : [item],
  );

  const reindexInfo = collectReindexInfoBeforeRemoval(
    items,
    (id) => findItemIn(getTrendStores(), id) as BaseItem | undefined,
  );

  _.forEach(items, _.flow(_.property('id'), cancelGroup));
  removeItemsFromTrendStores({ items, numPixels: Math.min(getStoredChartWidth(), getMaxSeriesPixels()) }, option);

  performReindexingAfterRemoval(reindexInfo, setCustomizationProps, () => getAlignableItems({}) as BaseItem[]);

  updateDerivedDataTree();

  _.chain(items)
    .reject('isChildOf')
    .reject(_.matchesProperty('itemType', ITEM_TYPES.SCALING_COLUMN))
    .map('id')
    .forEach((id) => addRecentlyAccessed(sqWorkbenchStore.stateParams.workbookId, id))
    .value();

  if (_.some(items, (item) => item.itemType === ITEM_TYPES.SERIES)) {
    fetchPlot()?.catch((e) => logWarn(formatMessage`Error reloading xy plot after removing item: ${e}`));
  }

  if (_.some(items, (item) => item.itemType === ITEM_TYPES.CONDITION)) {
    await Promise.all([
      exposedForTesting.fetchTableAndChartCapsules(),
      exposedForTesting.fetchChartCapsules(),
      exposedForTesting.fetchAllTimebarCapsules(),
      fetchTreemap(),
      fetchPlot(),
    ]).catch((e) => logWarn(formatMessage`Error reloading condition data after removing item: ${e}`));
  }

  if (
    _.some(items, (item) =>
      _.includes([ITEM_TYPES.SCALAR, ITEM_TYPES.SERIES, ITEM_TYPES.CONDITION, ITEM_TYPES.METRIC], item.itemType),
    )
  ) {
    fetchTable();
    fetchAnnotations().catch((e) => logWarn(formatMessage`Error fetching annotations after removing item: ${e}`));
  }

  removeGaps();
  updateLaneDisplay();

  if (sqTrendStore.capsuleTimeLimited.enabled) {
    exposedForTesting.addCapsuleTimeSegments();
  }

  if (_.some(items, ['id', sqInvestigateStore.item.id])) {
    clearInvestigatedItem();
  }

  if (_.some(items, (item) => item.itemType === ITEM_TYPES.SCALING_COLUMN)) {
    flux.dispatch('TREND_SET_SCALING_COLUMNS_UPDATED', true);
  }

  /**
   * Recursively find all ancestors of the item by searching all stores for children of the item.
   * @param {Object} item - The parent item
   * @returns {Object[]} Array of ancestors
   */
  function findAncestors(item: { id: string }): unknown[] {
    const ancestors = findChildrenIn(getTrendStores(), item.id);

    if (ancestors.length) {
      return ancestors.concat(_.flatMap(ancestors, findAncestors));
    } else {
      return _.compact(ancestors);
    }
  }
}

/**
 * Removes all items from the chart.
 */
export async function removeAllItems() {
  await exposedForTesting.removeItems(getAllItems({ itemTypes: ITEM_TYPES_WITH_COLUMNS }));
}

/**
 * Clears the chart by removing all items, canceling any ongoing operations, and resetting the chart defaults,
 * without resetting the duration.
 */
export function clear() {
  exposedForTesting.removeAllItems();
  exposedForTesting.removeTrendSelectedRegion();
  cancelAll();
}

/**
 * Removes all the selected items.
 *
 */
export function removeSelectedItems() {
  _.chain(getAllItems({ itemTypes: ITEM_TYPES_WITH_COLUMNS, excludeDummyItems: false }))
    .filter(['selected', true])
    .tap(exposedForTesting.removeItems)
    .value();
}

/**
 * Toggles the selection of an item.
 *
 * @param {Object} item - The item to toggle.
 */
export function toggleTrendItemSelected(item: { id: string; selected?: boolean }, forceTableRefresh = false) {
  exposedForTesting.setItemSelected(item, !item.selected, forceTableRefresh);
}

/**
 * Sets the selection of an item. Because the user or the select all button can trigger this function in rapid
 * succession all async calls are debounced.
 *
 * @param {Object} item - The item to toggle.
 * @param selected - True to select the item; false to unselect.
 * @param [forceTableRefresh] - True to refresh table/chart capsules; defaults to false.
 * @param [fetchTableData] - True to fetch data for the Simple or Condition Table, false to skip this. Defaults true.
 */
export function setItemSelected(
  item: { id: string; itemType?: string } | BaseItem,
  selected: boolean,
  forceTableRefresh = false,
  fetchTableData = true,
) {
  // Get item from the store to ensure item props are present (e.g. childType) because they may not be present
  // in the supplied item (e.g. when the item is initially added to the trend), fallback to use the provided item
  // if no store item can be found to ensure capsule selection of capsules not in the capsule table still works.
  item = findItemIn(getTrendStores(), item.id) || item;
  // If item is a composite child then replace it with the parent
  item = substituteParentforChild(item as { childType?: string | undefined });

  setSelectedInTrendStores({ item, selected, numPixels: getStoredChartWidth() });

  if (fetchTableData) {
    fetchTable();
  }

  const isConditionOrForceRefresh = item.itemType === ITEM_TYPES.CONDITION || forceTableRefresh;
  const isCapsule = item.itemType === ITEM_TYPES.CAPSULE;
  if (isConditionOrForceRefresh || (isCapsule && sqWorksheetStore.view.key === WORKSHEET_VIEW.SCATTER_PLOT)) {
    fetchConditionsDebounced();
  }
  if (sqInvestigateStore.activeTool === TREND_TOOLS.MANUAL_CONDITION && (isConditionOrForceRefresh || isCapsule)) {
    triggerManualConditionPreviewDebounced();
  }

  if (sqTrendStore.hideUnselectedItems) {
    fetchHiddenDebounced();
    if (item.itemType !== ITEM_TYPES.CAPSULE) {
      updateLaneDisplay();
    }

    const items = getAllItems({});
    if (selected) {
      // We've newly selected an item, so fetch its statistics
      fetchStatisticsDebounced(item);
      // And unset all non-selected items' statistics
      clearStatisticsForUnselectedItems(items);
    } else if (!items.some((item) => item.selected)) {
      // We've unselected the last selected item, so fetch all statistics
      fetchAllStatisticsDebounced();
    } else {
      // We've unselected the item, so unset its statistics
      clearStatisticsForUnselectedItems([{ ...item, selected: false }]);
    }
  }

  fetchCapsuleTimeDebounced();
}

/**
 * Handles selecting one or more items. MultiSelect mode is toggled by using the ctrl/meta key and causes the item
 * to be toggled without affecting the state of other items.  Without the modifier key there are two behaviors:
 * - If multiple items are selected then the item will now be the selected one, even if it was already selected.
 * - If only a single item is selected then the item's selection will be toggled.
 *
 * @param {Object} item - The item to be selected
 * @param {Object[]} items - The collection that this item belongs to
 * @param {Object} event - The click event
 */
export function selectTrendItems(
  item: any,
  items: Array<{ id?: string; childType?: string | undefined }>,
  event: { metaKey?: boolean; ctrlKey?: boolean },
) {
  let singleItemSelected;
  const multiSelect = isApplePlatform() ? event.metaKey : event.ctrlKey;
  const isCapsulePickingMode = item.itemType === ITEM_TYPES.CAPSULE && sqInvestigateStore.isCapsulePickingMode;
  if (isCapsulePickingMode && _.includes(sqTrendStore.allSelectedRegionOptionsPlugins, sqInvestigateStore.activeTool)) {
    return;
  }

  if (multiSelect || isCapsulePickingMode) {
    exposedForTesting.toggleTrendItemSelected(item);
  } else {
    // Clicking on a row (not checkbox) unselects all and toggle the single item
    // Capsules behave differently since selection can include elements not in items
    if (item.itemType === ITEM_TYPES.CAPSULE) {
      singleItemSelected = sqTrendCapsuleStore.selectedCapsules.length === 1;
      exposedForTesting.unselectAllCapsules();
    } else {
      ({ item, items } = processItems(item, items));
      singleItemSelected = _.filter(items, 'selected').length === 1;
      _.forEach(items, (item) => setSelectedInTrendStores({ item, selected: false }));
    }

    // If multiple items were previously selected, we want to select only the new item and clear other selections
    // (regardless of whether the passed in item was selected or not). If only one item was previously selected, we
    // want to toggle the item's selection status instead.
    if (singleItemSelected) {
      exposedForTesting.toggleTrendItemSelected(
        item,
        _.some(items, { selected: false, itemType: ITEM_TYPES.CONDITION }),
      );
    } else {
      exposedForTesting.setItemSelected(item, true, true);
    }
  }
}

/**
 * Substitutes the parent for any composite child in the supplied items. A composite child is one that
 * must be treated as part of the whole parent item from the perspective of trend selection. Returns the item and
 * items arguments unchanged if they do not contain a composite child.
 *
 * @param {Object} item - an item
 * @param {Object[]} items - an array of items
 * @returns {{item: any; items: any}} an object containing the possibly-updated item and items
 */
export function processItems(
  item: {
    childType?: string | undefined;
    selected?: boolean | undefined;
  },
  items: Array<{
    childType?: string | undefined;
  }>,
) {
  items = _.chain(items)
    .map((item) => substituteParentforChild(item))
    .uniq()
    .value();

  if (isCompositeChild(item)) {
    const parent = _.cloneDeep(substituteParentforChild(item)) as Record<string, unknown>;
    parent.selected = item.selected;
    item = parent;
  }
  return { item, items };
}

/**
 * If the supplied item is a composite child then the parent is returned instead. Otherwise the item is returned
 * unchanged.
 *
 * @param {Object} item - an item
 * @returns {Object} an item; possibly a parent in place of the supplied composite child.
 */
export function substituteParentforChild(item: { childType?: string; isChildOf?: string }) {
  if (isCompositeChild(item)) {
    return findItemIn(getTrendStores(), item.isChildOf!);
  } else {
    return item;
  }
}

/**
 * Unselects all capsules.
 */
export function unselectAllCapsules() {
  flux.dispatch('TREND_UNSELECT_ALL_CAPSULES');
  recomputeRuntimeChartItems();
  takeCapsulesFromSelection();
  if (sqTrendStore.hideUnselectedItems) {
    removeAllCapsuleSeries();
    // Reload capsules and data when we are in capsule time view and "Dimming" -> "Show only selected" is on.
    // Otherwise, we have no data for the unselected capsules.
    exposedForTesting.addCapsuleTimeSegments(true);
  }
  // trigger fetch data when we are in chain view
  exposedForTesting.createStitchDetails();
}

/**
 * Selects a set of capsules by dispatching a single event for all of them.
 * @param capsules - The set of capsules.
 */
export function selectCapsules(capsules: any[]) {
  setSelectedCapsulesInTrendStores({ capsules, selected: true, numPixels: getStoredChartWidth() });
}

/**
 * Compute the number of lanes to use for the chart. We don't want to sacrifice chart fidelity by choosing a
 * number that's too low, but choosing a huge number will result in more data than necessary being fetched
 * (CRAB-13743). Use the next common screen resolution width greater than or equal to the browser window so we don't
 * lose chart fidelity. If we go beyond resolutions listed (currently at 8k), double the value until
 * it exceeds the width of the browser.
 */
export function computeChartWidth(windowWidth: number): number {
  if (headlessCaptureMetadata().category === SeeqNames.Origins.Thumbnail) {
    // Thumbnails should always pretend to have a really wide chart width. This seems counter-intuitive because it
    // seems like we are asking appserver for extra data, however it reduces the laneWidth that is passed to
    // spikeCatcher which means that less data will need to be requested to to satisfy the lanes at the edges of
    // the display window. Which can save round trips to datasources because the data might be cached. See CRAB-15579
    return _.last(CHART_THRESHOLDS)!;
  }

  let threshold = _.reduceRight(
    CHART_THRESHOLDS,
    (currentThreshold, threshold) => (threshold >= windowWidth ? threshold : currentThreshold),
    _.last(CHART_THRESHOLDS)!,
  );

  while (windowWidth > threshold) {
    // The window is using more pixels than the largest resolution listed in chartThresholds.
    threshold *= 2;
  }

  return threshold;
}

/**
 * Fetches timeseries data based on the current view.
 */
export function fetchDataBasedOnView(requests: any[] = []) {
  switch (sqTrendStore.view) {
    case TREND_VIEWS.CALENDAR:
      requests.push(exposedForTesting.fetchAllTimeseries());
      break;
    case TREND_VIEWS.CHAIN:
      requests.push(exposedForTesting.createStitchDetails());
      break;
    case TREND_VIEWS.CAPSULE:
      requests.push(exposedForTesting.addCapsuleTimeSegments(true));
      requests.push(exposedForTesting.fetchAllTimeseries());
      break;
  }
}

/**
 * Sets the width of the chart and then re-fetches timeseries data when the browser size increases
 * beyond a CHART_THRESHOLD. Always resize chartWidth to a value greater than or equal to the number of pixels
 * of the chart so wo don't lose chart fidelity (CRAB-13743).
 *
 * @param windowWidth - The width of the window
 * @param forceSetForTests - Normally width is not set if the size decreases, but this can be overridden for tests
 */
export function setChartWidth(windowWidth = window.innerWidth, forceSetForTests = false) {
  if (headlessRenderMode()) {
    // CRAB-26006 - in headless mode, page.setViewport may be called to adjust the width, so that the screenshot
    // contains the whole content (e.g. very wide table). In this case we do not want to trigger any data fetch.
    return;
  }

  const newChartWidth = computeChartWidth(windowWidth);
  sqDurationStore.actions.setDisplayPixels(newChartWidth);

  if (newChartWidth <= getStoredChartWidth() && !forceSetForTests) {
    // Only need to fetch (downsampled) data if chart is getting larger than threshold.
    return;
  }

  recordChartWidth(newChartWidth);

  const fetchRequests = [fetchAllFftTables(), fetchMinimapSignals()];
  exposedForTesting.fetchDataBasedOnView(fetchRequests);
  return Promise.all(fetchRequests);
}

/**
 * Returns the chart width in pixels.
 *
 * returns the chart width in pixels.
 */
export function getChartWidth(): number {
  return getStoredChartWidth();
}

/**
 * Fetch the data for all items used by the different charts and views. Any updates to this method should also
 * considered in the context fetchItems.
 *
 * @param {Object} [options] - Options to configure what is fetched
 * @param {Boolean} [options.skipProps] - Skip the fetching of properties
 * @param {Boolean} [options.skipTimebar] - Skip fetching timebar capsules
 *
 * @returns {Promise} A promise that resolves when all the data is fetched.
 */

export function fetchAllItems({ skipProps = false, skipTimebar = false } = {}) {
  // None of this async data is needed in an organizer topic
  if (sqWorkbookStore.isTopic) {
    return Promise.resolve([]);
  }

  const promises = [];
  const fetchPropsPromise = skipProps
    ? Promise.resolve()
    : exposedForTesting.fetchPropsForAllItems(true).catch((e) => !isCanceled(e) && logError(e));
  // Treemap needs to know assets before it can fetch and table needs to know assets and if signal is string
  promises.push(fetchPropsPromise.then(() => Promise.all([fetchTreemap(), fetchTable(), updateDerivedDataTree()])));
  promises.push(fetchPlot());
  if (!skipProps) {
    promises.push(fetchMonitoredConditionIds(sqTrendConditionStore.items.map((item) => item.id)));
  }

  if (sqTrendStore.view !== TREND_VIEWS.CAPSULE) {
    promises.push(
      fetchPropsPromise.then(() =>
        sqTrendStore.view === TREND_VIEWS.CHAIN
          ? exposedForTesting.fetchAllStatistics(true)
          : exposedForTesting.fetchAllTimeseries(!skipProps),
      ),
    );
  }
  const isChainOrCapsuleView = sqTrendStore.view === TREND_VIEWS.CHAIN || sqTrendStore.view === TREND_VIEWS.CAPSULE;
  promises.push(isChainOrCapsuleView ? undefined : exposedForTesting.fetchPreviewSeries());

  promises.push(fetchAllTables());
  promises.push(exposedForTesting.fetchAllScalars(!skipProps));
  // The request for capsules must be done before the request for capsules table. Otherwise the warnings are
  // swallowed by the last one. This specific order is needed until CRAB-8020 will be solved.
  flux.dispatch('TREND_SET_CAPSULE_PANEL_IS_LOADING', { isLoading: true }, PUSH_IGNORE);
  promises.push(
    exposedForTesting.fetchChartCapsules().then(() => {
      exposedForTesting.fetchTableAndChartCapsules();
    }),
  );
  promises.push(skipTimebar ? undefined : exposedForTesting.fetchAllTimebarCapsules());

  return Promise.all(promises);
}

/**
 * Fetch the data for items used by the different charts and views. Any updates to this method should also
 * considered in the context of fetchAllItems.
 *
 * @param {Object[]} items - Array of items currently in the chart
 * @param {Object} options - Options for fetching
 * @param {boolean} options.fetchFailed - True to force it to also fetch FAILED conditions
 * @param {boolean} options.fetchCapsulesLater - True to skip fetching capsules before fetching everything else
 * @returns {Promise} A promise that resolves when all the data is fetched.
 */
export function fetchItems(items: any[], options = { fetchFailed: false, fetchCapsulesLater: false }) {
  const isConditionPresent = _.some(items, ['itemType', ITEM_TYPES.CONDITION]);
  return (
    isConditionPresent && !options.fetchCapsulesLater
      ? // A workaround for the fact that warnings do not show for cached data and conditions fire off three requests
        // (chart, timebar, and table) which means two of those will use cached data. So, to allow the user to see
        // formula warnings when updating a formula the chart capsules complete first (CRAB-11702).
        exposedForTesting.fetchChartCapsules(_.filter(items, ['itemType', ITEM_TYPES.CONDITION]))
      : // In the case where a users items are completely errored out, it is very confusing to have the capsules load
        // first, so in that case we postpone retrieving chart capsules until later on (CRAB-16686)
        Promise.resolve()
  ).then(() => {
    const propertyPromises: Promise<unknown>[] = [];
    const promises: Promise<unknown>[] = [];

    _.forEach(items, (item: any) => {
      if (item.itemType !== ITEM_TYPES.SCALING_COLUMN) {
        propertyPromises.push(exposedForTesting.fetchItemProps(item.id));
      }
      switch (item.itemType) {
        case ITEM_TYPES.CONDITION:
          if (options.fetchCapsulesLater) {
            promises.push(exposedForTesting.fetchChartCapsules([item]));
          }
          promises.push(exposedForTesting.fetchTimebarCapsules(item.id));
          promises.push(exposedForTesting.fetchStatistics(item));
          break;
        case ITEM_TYPES.SERIES:
          promises.push(exposedForTesting.fetchTimeseries(item.id));
          break;
        case ITEM_TYPES.SCALAR:
          promises.push(exposedForTesting.fetchScalar(item.id));
          break;
        case ITEM_TYPES.TABLE:
          promises.push(fetchTableData(item.id));
          break;
        case ITEM_TYPES.FORMULA_ITEM:
          if (item.resultType === SeeqNames.FormulaType.StatsSummary) {
            promises.push(refreshStatsData(item.id));
          }
          break;
      }
    });

    promises.push(
      Promise.all(propertyPromises).then(function () {
        return [
          // Properties must be set before we can update the derived data tree
          updateDerivedDataTree(),
          // Assets must be fetched before treemap can be updated
          fetchTreemap(),
        ];
      }),
    );

    const statisticsDisplayed = _.chain(items)
      .map('id')
      .intersection(_.map(sqTrendStore.customColumns(TREND_PANELS.CAPSULES), 'referenceSeries'))
      .some()
      .value();

    if (isConditionPresent || statisticsDisplayed) {
      promises.push(exposedForTesting.fetchTableAndChartCapsules(options));
    }

    promises.push(fetchPlot() ?? Promise.resolve());
    promises.push(fetchTable());

    return Promise.all(promises);
  });
}

/**
 * HIDDEN_FROM_TREND is set when an item is hidden from the trend rather than fetching data that won't be seen. If
 * the item becomes visible again, this method handles loading the data that we deferred loading. This is
 * essentially a slimmed down version of fetchItems
 */
export function fetchHiddenTrendData() {
  const [conditions, otherItems] = _.chain(
    getAllItems({
      itemChildrenTypes:
        sqTrendStore.view === TREND_VIEWS.CAPSULE
          ? [ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE]
          : [ITEM_CHILDREN_TYPES.METRIC_DISPLAY, ITEM_CHILDREN_TYPES.METRIC_THRESHOLD],
    }),
  )
    .filter((item) => getItemDataStatus(item) === ITEM_DATA_STATUS.HIDDEN_FROM_TREND)
    .partition(['itemType', ITEM_TYPES.CONDITION])
    .value();
  const promises = [];

  if (_.some(conditions)) {
    promises.push(exposedForTesting.fetchChartCapsules(conditions));
  }
  _.forEach(otherItems, (item: any) => {
    switch (item.itemType) {
      case ITEM_TYPES.SERIES:
        promises.push(exposedForTesting.fetchTimeseries(item.id));
        break;
      case ITEM_TYPES.SCALAR:
        promises.push(exposedForTesting.fetchScalar(item.id));
        break;
      case ITEM_TYPES.TABLE:
        promises.push(fetchTableData(item.id));
        break;
    }
  });

  return Promise.all(promises);
}

/**
 * Fetches the specified item and all of its calculation dependencies
 *
 * @param {String} id - ID of item
 *
 * @returns {Promise} that resolves when the item and all of its dependencies have been fetched
 */
export function fetchItemAndDependents(id: string) {
  return sqItemsApi.getFormulaDependents({ id }).then(({ data }) =>
    _.chain(data.items)
      .map('id')
      .concat(id)
      .thru((ids) => {
        const children = _.chain(getAllChildItems({})).filter((item) =>
          _.some(['interestId', 'shadedAreaLower.id', 'shadedAreaUpper.id'], (path) =>
            _.includes(ids, _.get(item, path)),
          ),
        );

        const parentIds = children.map('isChildOf').value();
        return _.concat(ids, parentIds);
      })
      .uniq()
      .map((itemId) => findItemIn(getTrendStores(), itemId))
      .compact()
      .thru((items) => exposedForTesting.fetchItems(items))
      .value(),
  );
}

/**
 * Fetches "non capsule series" data (such as signals in the details panel).
 *
 * Computes a unique cancellation group name based on the
 * items so that if the another set of requests is made for the same items the first are canceled before fetching
 * the next batch. Calls to this function should still be debounced to ensure that there are not repeated calls
 * and cancels.
 *
 * @param {boolean} skipChildren - if true, no data is fetched for child items
 *
 * @return {Promise} A promise that resolves when all non-capsule series have been fetched
 */
export function fetchAllTimeseries(skipChildren?: boolean) {
  const items =
    sqTrendStore.view === TREND_VIEWS.CAPSULE
      ? getCapsuleSeries()
      : groupedNonCapsuleSeries({
          nonCapsuleSeries: sqTrendSeriesStore.nonCapsuleSeries,
        });

  return _.chain(items)
    .reject((item) => isItemRedacted(item))
    .reject((item: any) => skipChildren && item.isChildOf)
    .map((item: any) => exposedForTesting.fetchTimeseries(item.id))
    .thru((promises) => Promise.all(promises))
    .value();
}

/**
 * Fetches the timeseries data and then updates statistics. Handles both regular series and series converted from
 * capsules since the start/end times and fetched series are dependent on that property. This method is
 * dependent on the `chartWidth` variable because that determines how much data to fetch.
 * This method also loads any custom properties specified by the user. Properties are only loaded once as they are
 * assigned to the asset and don't change as the time range changes.
 *
 * @param {String} itemId - The id of the series item to fetch.
 *
 * @return {Promise} Promise that is resolved with the plot values for the series
 */
export function fetchTimeseries(itemId: string): Promise<any> {
  let item = sqTrendSeriesStore.findItem(itemId);
  const cancellationGroup = `fetchTimeseries${CANCELLATION_GROUP_GUID_SEPARATOR}${itemId}`;
  const arrivalFetch = beginArrivalFetch(cancellationGroup);

  // If loading for the first time we won't have a chartWidth, so no need to fetch data that will be overwritten as
  // soon as the chart is instantiated.
  if (!_.isNumber(getStoredChartWidth())) {
    return Promise.resolve();
  }

  if (sqWorksheetStore.view.key !== WORKSHEET_VIEW.TREND) {
    return Promise.resolve();
  }

  if (
    isHidden({
      item,
    })
  ) {
    setSeriesDataStatusHiddenFromTrend(item.id);
    return Promise.resolve();
  }

  const numPixels = Math.min(getStoredChartWidth(), getMaxSeriesPixels());

  if (!_.includes([WORKSHEET_VIEW.TREND, WORKSHEET_VIEW.TABLE], sqWorksheetStore.view.key)) {
    // Scatterplot and Treemap views fetch their own data
    return Promise.resolve();
  }

  if (sqTrendStore.view === TREND_VIEWS.CAPSULE && item.childType !== ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE) {
    // In capsule view, Series from Capsule segments are be passed instead of the parent signal
    return Promise.resolve();
  }

  if (sqTrendStore.view !== TREND_VIEWS.CAPSULE && item.childType === ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE) {
    // Series from Capsule segments should never be fetched outside of chain view
    return Promise.resolve();
  }

  // Reset all enabled statistics for this item before requesting new values
  sqItemRuntimeStore.actions.setStatistics(item.id, {});
  const range: RangeExport = {
    start: getDurationDisplayRange().start,
    end: getDurationDisplayRange().end,
    duration: moment.duration(
      sqDurationStore.getState().displayRange.end - sqDurationStore.getState().displayRange.start,
    ),
  };

  // Items that specify fragments are formula functions that will be evaluated by fetchOneSignal
  if (sqTrendStore.view !== TREND_VIEWS.CHAIN || item.fragments) {
    setSeriesDataStatusLoading(item.id);
    cancelGroup(cancellationGroup, true);

    let seriesRequest;
    if (item.shadedAreaUpper && item.shadedAreaLower) {
      seriesRequest = fetchPairedSignal(
        item.shadedAreaLower,
        item.shadedAreaUpper,
        range,
        numPixels,
        cancellationGroup,
      );
    } else {
      // This is a "normal" signal in the Trend view
      seriesRequest = fetchOneSignal(getItemId(item), range, numPixels, item.fragments, cancellationGroup);
    }

    return seriesRequest
      .then((results) =>
        runArrivalCommit(() => {
          if (!sqTrendStore.isRegionSelected) {
            exposedForTesting.updateStatistics(item, results);
          } else {
            // this could be a result of a reload, so we need to fetch the statistics for the selected region.
            // The workstep doesn't persist valueUnitOfMeasure, so the item snapshot may be stale (still
            // missing it); use the freshly-fetched UOM so string signals are correctly detected before the
            // stats formula is built. SUP-68995.
            exposedForTesting.fetchStatistics({
              ...item,
              valueUnitOfMeasure: results.valueUnitOfMeasure ?? item.valueUnitOfMeasure,
            });
          }

          const payload = _.assign(
            { id: item.id },
            _.pick(results, [
              'samples',
              'valueUnitOfMeasure',
              'timingInformation',
              'meterInformation',
              'warningLogs',
              'warningCount',
              'interpolationMethod',
            ]),
          );
          recordDataStatus(ITEM_DATA_STATUS.PRESENT, payload);
          // _.pick over the union of result shapes infers PartialDeep, which makes sample keys optional;
          // the runtime shape always has them, and the dispatch this replaced was untyped
          recordSeriesData(payload as unknown as AddDataPayload, PUSH_IGNORE);

          if (item.childType === ITEM_CHILDREN_TYPES.METRIC_DISPLAY) {
            const parentMetric = sqTrendMetricStore.findItem(item.isChildOf);
            setMetricDataStatusPresent(
              _.assign({ id: parentMetric.id }, _.pick(results, ['warningLogs', 'warningCount'])),
            );
          }

          updateLaneDisplay();

          return payload;
        }, arrivalFetch),
      )
      .catch(_.partial(exposedForTesting.catchItemDataFailure, item.id, cancellationGroup));
  } else {
    // sqTrendStore.view === TREND_VIEWS.CHAIN: this is "Chain View"
    if (_.isEmpty(sqCapsuleRuntimeStore.getState().stitchTimes)) {
      return Promise.resolve();
    }

    return (
      item.shadedAreaUpper && item.shadedAreaLower
        ? fetchPairedSignalWithinCondition(
            item.isChildOf,
            item.shadedAreaLower,
            item.shadedAreaUpper,
            getChainViewLaneWidth(numPixels),
            cancellationGroup,
          )
        : fetchOneSignalWithinCondition(getItemId(item), numPixels, cancellationGroup)
    )
      .then(function (data) {
        if (!sqTrendStore.isRegionSelected) {
          exposedForTesting.updateStatistics(item, data);
        } else {
          // this could be a result of a reload, so we need to fetch the statistics for the selected region.
          // The workstep doesn't persist valueUnitOfMeasure, so the item snapshot may be stale (still
          // missing it); use the freshly-fetched UOM so string signals are correctly detected before the
          // stats formula is built. SUP-68995.
          exposedForTesting.fetchStatistics({
            ...item,
            valueUnitOfMeasure: data.valueUnitOfMeasure ?? item.valueUnitOfMeasure,
          });
        }
        const payload = _.assign(
          {
            id: itemId,
            valueUnitOfMeasure: data.valueUnitOfMeasure,
            samples: data.samples,
          },
          _.pick(data, ['timingInformation', 'meterInformation', 'warningLogs', 'warningCount', 'interpolationMethod']),
        );

        recordDataStatus(ITEM_DATA_STATUS.PRESENT, payload);
        recordSeriesData(payload, PUSH_IGNORE);
        updateLaneDisplay();
        return payload;
      })
      .catch(_.partial(exposedForTesting.catchItemDataFailure, item.id, cancellationGroup));
  }
}

/**
 * Fetches a set of signal segments in one request by creating a manual condition with all the capsules. This
 * method is dependent on the `chartWidth` variable because that determines how much data to fetch.
 *
 * @param itemId - The id of the series item to fetch.
 * @param capsules - A set of capsules used to determine the signal segments which will be loaded.
 * @return {Promise} Promise that is resolved with the plot values for the series
 */
export function fetchSignalSegments(itemId: string, capsules: CapsuleItem[]) {
  if (_.isEmpty(capsules) || !_.isNumber(getStoredChartWidth())) {
    return Promise.resolve();
  }

  setSeriesDataStatusLoading(itemId);

  const cancellationGroup = `loadSignalSegments${CANCELLATION_GROUP_GUID_SEPARATOR}${itemId}`;

  const item = sqTrendSeriesStore.findItem(itemId);
  const stats = _.chain(item.itemType === ITEM_TYPES.SERIES ? TREND_SIGNAL_STATS : TREND_CONDITION_STATS)
    .filter((stat: any) => sqTrendStore.isColumnEnabled(TREND_PANELS.SERIES, stat.key))
    .filter((stat: any) => (isStringSeries(item) ? stat.isStringCompatible : true))
    .value();
  const enums = stats.length ? `, ${_.map(stats, 'stat').join(', ')}` : '';

  cancelGroup(cancellationGroup, true);
  const { formula, limit, capsulesQueryIntervals, range } = buildSignalSegmentsFormula(
    itemId,
    capsules as Array<{ startTime: number; endTime: number }>,
    getStoredChartWidth(),
    getLongestCapsuleSeriesDuration(),
    enums,
  );

  if (!formula && sqTrendStore.view === TREND_VIEWS.CAPSULE) {
    resetTrendCapsuleTimeOffsets();
    return Promise.resolve();
  }

  return computeSamples({
    usePost: true, // Needed in case the condition formula is long
    id: itemId,
    range,
    formula,
    limit,
    cancellationGroup,
  })
    .then((result) => {
      const {
        samples,
        valueUnitOfMeasure,
        meterInformation,
        timingInformation,
        warningCount,
        warningLogs,
        interpolationMethod,
        table,
      } = result;
      recordDataStatus(ITEM_DATA_STATUS.PRESENT, {
        id: itemId,
        timingInformation,
        meterInformation,
        warningCount,
        warningLogs,
      });
      recordSignalSegmentsData(
        {
          requestCapsules: capsulesQueryIntervals,
          id: itemId,
          samples,
          valueUnitOfMeasure,
          interpolationMethod,
        },
        PUSH_IGNORE,
      );
      if (table && table.data) {
        updateStatistics(item, result);
      }
    })
    .catch(_.partial(exposedForTesting.catchItemDataFailure, itemId, cancellationGroup));
}

/**
 * Updates the series statistics with those returned by spikecatcher.
 *
 * @param item - The item to be updated
 * @param results - the results returned by spikecatcher
 */
export function updateStatistics(
  item: SeriesItem & { childType?: string; isChildOf?: string },
  results: {
    valueUnitOfMeasure?: string;
    table?: { headers: any[]; data: unknown[][] };
    meterInformation?: any;
  },
): void {
  if (!results.table) {
    return;
  }

  const enabledStats = TREND_SIGNAL_STATS.filter((stat) => sqTrendStore.isColumnEnabled(TREND_PANELS.SERIES, stat.key));
  const statistics = extractStatsFromTable(results.table, enabledStats, results.valueUnitOfMeasure);

  if (item.childType === ITEM_CHILDREN_TYPES.METRIC_DISPLAY && item.isChildOf) {
    sqItemRuntimeStore.actions.setStatistics(sqTrendMetricStore.findItem(item.isChildOf).id, statistics);
  } else {
    sqItemRuntimeStore.actions.setStatistics(item.id, statistics);
  }
}

/**
 * Fetch a "normal" (unpaired) signal and return a promise with the results.
 *
 * @param seriesId - the series ID
 * @param {Object} range - Object container for range arguments
 * @param {Moment|Number} range.start - start of the range
 * @param {Moment|Number} range.end - end of the range
 * @param {Number} range.duration - duration of the time range in milliseconds
 * @param {Number} numPixels - the number or horizontal pixels avaliable for signal display
 * @param {Object} fragments - A formula fragment object where the keys are the names of unbound formula export function
 * variables and the values are constants identifying the formula fragments that should be used.
 * @param {String} cancellationGroup - the group used to cancel the requests
 * @returns {Promise} a promise that resolves with the fetched signal
 */
export function fetchOneSignal(
  seriesId: string,
  range: RangeExport,
  numPixels: number,
  fragments: Record<string, any> | undefined,
  cancellationGroup: string,
) {
  const laneWidth = `${getMSPerPixelWidth(range.duration, numPixels)}ms`;

  const downSampleFormula = sqTrendStore.buildDownsampleFormula(laneWidth, seriesId, undefined, getEnabledColumns());
  return computeSamples(
    _.omitBy(
      {
        id: seriesId,
        range,
        fragments: assignFragmentFormulas(fragments, laneWidth),
        formula: `$series${sqTrendStore.buildSummarizeFormula(seriesId)}${downSampleFormula}`,
        limit: numPixels * SPIKECATCHER_PER_PIXEL,
        cancellationGroup,
      },
      _.isUndefined,
    ),
  ).then((results) => ({
    valueUnitOfMeasure: results.valueUnitOfMeasure,
    samples: results.samples,
    table: results.table,
    timingInformation: results.timingInformation,
    meterInformation: results.meterInformation,
    warningLogs: results.warningLogs,
    warningCount: results.warningCount,
    interpolationMethod: results.interpolationMethod,
  }));
}

/**
 * Replaces a FORMULA_FRAGMENT_TYPE constant with the actual formula fragment that should be used by the backend
 * to evaluate the formula function.
 *
 * @param {Object} fragments - A formula fragment object where the keys are the names of unbound formula function
 * variables and the values are constants identifying the formula fragments that should be used.
 * @param {string} laneWidth - a string representation of the lane width in milliseconds
 * @returns {Object} A formula fragment object where the keys are the names of unbound formula function variables
 *   and the values are the corresponding formula fragments that are used to compute the value of the variable.
 */
export function assignFragmentFormulas(
  fragments: Record<string, unknown> | undefined,
  laneWidth: string,
): Record<string, string> | undefined {
  if (!fragments) {
    return undefined;
  }

  const result: Record<string, string> = {};
  _.forEach(fragments, (value, key) => {
    if (value === FORMULA_FRAGMENT_TYPE.DISPLAY_RANGE) {
      result[key] = getCapsuleFormula(getDurationDisplayRange());
      // Also needs the laneWidth to be assigned so the backend can run spikeCatcher
      result.laneWidth = laneWidth; // expecting milliseconds
    }
  });

  return !_.isEmpty(result) ? result : undefined;
}

/**
 * Create a condition formula based on the stitchTimes. The resulting condition contains
 * only the capsules that are being displayed in chain view
 *
 * @param {String} [seriesId] - the series ID that should be used for filtering stitchTimes in grouping mode
 */
export function createFormulaCondition(seriesId?: string) {
  // If capsule group mode is enabled then only signals that are paired to a condition are shown during the
  // conditions capsules. This requires filtering of the capsules by condition and then explicit loading of only
  // the grouped signals.
  let capsules: Array<{ start: number; end: number }> | undefined;
  if (sqWorksheetStore.capsuleGroupMode && seriesId) {
    const conditionIds = _.keys(sqWorksheetStore.conditionToSeriesGrouping).filter((k) =>
      _.includes(sqWorksheetStore.conditionToSeriesGrouping[k], seriesId),
    );
    capsules = _.filter(sqCapsuleRuntimeStore.getState().capsulesTimings, (c) => _.includes(conditionIds, c.isChildOf));
  }

  return conditionFormula(
    _.map(_.isEmpty(capsules) ? sqCapsuleRuntimeStore.getState().stitchTimes : capsules, ({ start, end }) => ({
      startTime: start,
      endTime: moment.isMoment(end) ? end.valueOf() : end,
      properties: [],
    })),
  );
}

/**
 * Fetch a "normal" (unpaired) signal within a given condition and return a promise with the results.
 *
 * @param {String} seriesId - the series ID
 * @param {Number} numPixels - the number or horizontal pixels available for signal display
 * @param {String} cancellationGroup - the group used to cancel the requests
 * @returns {Promise} a promise that resolves with the fetched signal
 */
export function fetchOneSignalWithinCondition(seriesId: string, numPixels: number, cancellationGroup: string) {
  const range = getDurationDisplayRange();
  const useablePixels = getChainViewUseablePixels(numPixels);
  const laneWidth = getChainViewLaneWidth(numPixels);
  const downSampleFormula = sqTrendStore.buildDownsampleFormula(
    laneWidth,
    seriesId,
    createFormulaCondition(seriesId),
    getEnabledColumns(),
  );
  return computeSamples({
    usePost: true, // Needed in case the condition formula is long
    id: seriesId,
    range,
    formula: `$series${sqTrendStore.buildSummarizeFormula(seriesId)}${downSampleFormula}`,
    limit: useablePixels * SPIKECATCHER_PER_PIXEL,
    cancellationGroup,
  });
}

/**
 * Computes the lane width (the time each horizontal pixel represents) for Chain View. In Chain View the x-axis only
 * spans the stitched capsules, so the lane width must be derived from the total stitched duration rather than from
 * the display range.
 *
 * @param numPixels - the number of horizontal pixels available for signal display
 * @returns the lane width as a formula duration string, e.g. "25000ns"
 */
export function getChainViewLaneWidth(numPixels: number): string {
  // Total all durations so we can figure out percentage of total chartWidth to be used for each period
  const durationTotal = _.sumBy(sqCapsuleRuntimeStore.getState().stitchTimes, 'duration');
  const useablePixels = getChainViewUseablePixels(numPixels);
  return `${NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND * getMSPerPixelWidth(durationTotal, useablePixels)}ns`;
}

/**
 * The number of horizontal pixels available for signal data in Chain View, i.e. the chart width less the pixels
 * used for the breaks between capsules.
 *
 * @param numPixels - the number of horizontal pixels available for signal display
 */
export function getChainViewUseablePixels(numPixels: number): number {
  const useablePixels = numPixels - PIXELS_PER_BREAK * sqCapsuleRuntimeStore.getState().stitchBreaks.length;
  if (useablePixels === 0) {
    throw new Error('Not enough usable pixels - would have have divided by zero');
  }
  return useablePixels;
}

function getCapsuleCustomPropertyValuesAndUOMs(
  capsule: CapsuleFormulaTableRow,
  capsuleHeaders: TableColumnOutputV1[],
  customColumnKeys: string[],
): CustomPropertyValuesAndUOMs & { statistics?: AnyProperty<string | number> } {
  return _.chain(customColumnKeys)
    .reduce(
      (allProps, customColumnKey) => {
        const [_ignored, topKey, subKey] = customColumnKey.match(/^([^.]+)\.(.+)$/) ?? [];
        const value = capsule[customColumnKey as `${'properties' | 'statistics'}.${string}`];
        if (_.isNil(value) || !topKey) {
          return allProps;
        }

        return topKey === 'statistics'
          ? _.merge(allProps, { statistics: { [subKey]: value } })
          : _.merge(allProps, {
              properties: { [subKey]: value },
              propertiesUOM: { [subKey]: _.find(capsuleHeaders, { name: subKey })?.units },
            });
      },
      { properties: {}, propertiesUOM: {}, statistics: {} },
    )
    .omitBy(_.isEmpty)
    .value();
}

/**
 * Fetches the capsules for a list of conditions and then adds the capsules to the store. Data for each capsule
 * is provided either through one of the predefined TREND_COLUMNS or a custom column definition.
 *
 * Adds table capsules to chart if view is capsule time
 *
 * @param {Object} options - Options for fetching
 * @param {boolean} options.fetchFailed - True to force it to also fetch FAILED conditions
 *
 * @returns {Promise} Resolves with the capsule data
 */
export function fetchTableAndChartCapsules(options = { fetchFailed: false }): Promise<void> {
  if (
    !window.location.pathname.includes('/builder') &&
    (!sqWorkbenchStore.stateParams.worksheetId ||
      _.includes([WORKSHEET_VIEW.TABLE, WORKSHEET_VIEW.TREEMAP], sqWorksheetStore.view.key) ||
      sqWorkbookStore.isTopic)
  ) {
    return Promise.resolve();
  }

  const cancellationGroup = 'tableCapsules';
  const allConditions = getAllItems({
    // Ensure that failing conditions do not fail the whole request
    excludeDataStatus: options.fetchFailed
      ? [ITEM_DATA_STATUS.REDACTED] // Only retry REDACTED items by refreshing the page
      : [ITEM_DATA_STATUS.REDACTED, ITEM_DATA_STATUS.FAILURE, ITEM_DATA_STATUS.CANCELED],
    workingSelection: true,
    excludeEditingCondition: true,
    excludeDummyItems: true,
    itemTypes: [ITEM_TYPES.CONDITION],
  });
  // This handles the case that, in dimming mode, no conditions are selected, but
  // another item is selected, preventing the condition from being shown. However,
  // chain view and capsule time don't make sense without conditions so in those views
  // populate the table with all the conditions even though none are selected.
  const conditionIds = allConditions
    .filter(
      (item) =>
        !(
          sqTrendStore.view === TREND_VIEWS.CALENDAR &&
          isHidden({
            item,
          })
        ),
    )
    .map(getItemId);

  const range = getDurationDisplayRange();
  const initialSort = sqTrendStore.getPanelSort(TREND_PANELS.CAPSULES);
  const sort: TableSortParams = {
    sortAsc: initialSort.sortAsc,
    sortBy: initialSort.sortBy,
    orderedAdditionalSortPairs: [
      {
        sortBy: 'conditionId',
        sortAsc: true,
      },
    ],
  };
  const offset = sqTrendStore.capsulePanelOffset;

  if (_.isEmpty(conditionIds)) {
    // No conditions would have been fetched, clear the table
    addCapsulesToTrendStores(
      {
        capsules: [],
        numPixels: Math.min(getStoredChartWidth(), getMaxSeriesPixels()),
      },
      PUSH_IGNORE,
    );
    takeCapsulesFromSelection();
    // Clear the Capsule pane loading spinner
    flux.dispatch('TREND_SET_CAPSULE_PANEL_IS_LOADING', { isLoading: false }, PUSH_IGNORE);
    flux.dispatch('TREND_SET_CAPSULE_PANEL_HAS_NEXT', { hasNext: false }, PUSH_IGNORE);
    flux.dispatch('TREND_RESET_CAPSULE_PANEL_OFFSET', {}, PUSH_IGNORE);
    return Promise.resolve();
  }
  const { allDecoratedPropertyColumns, allDecoratedStatColumns, customColumnKeys } = getPropertyAndStatisticsColumns();

  flux.dispatch('TREND_SET_CAPSULE_PANEL_IS_LOADING', { isLoading: true }, PUSH_IGNORE);
  return cancelGroup(cancellationGroup)
    .then(() =>
      fetchCapsulesPaneTable({
        columns: {
          propertyColumns: allDecoratedPropertyColumns,
          statColumns: allDecoratedStatColumns,
        },
        range: { start: range.start.valueOf(), end: range.end.valueOf() },
        buildAdditionalFormula: getBuildAdditionalFormula(allDecoratedPropertyColumns, allDecoratedStatColumns, true),
        itemIds: conditionIds,
        sortParams: sort,
        offset,
        limit: CAPSULES_PER_PAGE,
        cancellationGroup,
      }),
    )
    .then((results) => {
      const capsulesToAdd: Array<Record<string, unknown>> = [];
      flux.dispatch('TREND_SET_CAPSULE_PANEL_HAS_NEXT', { hasNext: results.data?.hasNextPage }, PUSH_IGNORE);
      _.forEach(results.data?.table, (capsule) => {
        const condition = sqTrendConditionStore.findItem(capsule.conditionId);
        if (condition) {
          const capsuleToPush: Record<string, unknown> = {
            id: capsule.capsuleId ?? (capsule as any)['Capsule ID'],
            isChildOf: condition.id,
            childType: ITEM_CHILDREN_TYPES.CAPSULE,
            conditionName: condition.name,
            conditionId: condition.id,
            color: condition.color,
            isUncertain: capsule.isUncertain,
            cursorKey: capsule.cursorKey,
            isReferenceCapsule: capsule.isReferenceCapsule,
            startTime: _.get(capsule, 'startTime', range.start),
            endTime: _.get(capsule, 'endTime', range.end),
            notFullyVisible: !isCapsuleFullyVisible(capsule.startTime, capsule.endTime),
            similarity: capsule.similarity,
          };

          _.assign(
            capsuleToPush,
            exposedForTesting.getCapsuleCustomPropertyValuesAndUOMs(capsule, results.data?.headers, customColumnKeys),
          );

          _.forEach(CAPSULE_PANEL_LOOKUP_COLUMNS, (column) => {
            if (sqTrendStore.isColumnEnabled(TREND_PANELS.CAPSULES, column.key) && column.accessor) {
              capsuleToPush[column.accessor] = (condition as Record<string, unknown>)[column.accessor];
            }
          });

          // If the display range has not changed (as a result of a growing capsule) - we need to fetch the chart
          // capsules
          capsulesToAdd.push(capsuleToPush);
        }
      });

      addCapsulesToTrendStores(
        {
          capsules: capsulesToAdd as CapsuleToAdd[],
          numPixels: Math.min(getStoredChartWidth(), getMaxSeriesPixels()),
        },
        PUSH_IGNORE,
      );
      takeCapsulesFromSelection();

      const headers = results.data.headers;
      _.forEach(CAPSULE_PANEL_LOOKUP_COLUMNS, (column: PropertyColumn) => {
        if (sqTrendStore.isColumnEnabled(TREND_PANELS.CAPSULES, column.key)) {
          headers.push({ name: column.title!, type: column.style });
        }
      });

      flux.dispatch('TREND_SET_PROPERTY_COLUMNS', { headers }, PUSH_IGNORE);

      exposedForTesting.removeItems(
        _.reject(getCapsuleSeries(), (series: any) => findCapsuleItem(series.capsuleId)),
        PUSH_IGNORE,
        false,
      );

      setTrendColorMode();

      flux.dispatch('TREND_SET_CAPSULE_PANEL_IS_LOADING', { isLoading: false }, PUSH_IGNORE);
    })
    .catch((e) => {
      if (!isCanceled(e)) {
        flux.dispatch('TREND_SET_CAPSULE_PANEL_IS_LOADING', { isLoading: false }, PUSH_IGNORE);
        addCapsulesToTrendStores(
          {
            capsules: [],
            numPixels: Math.min(getStoredChartWidth(), getMaxSeriesPixels()),
          },
          PUSH_IGNORE,
        );
        takeCapsulesFromSelection();

        if (isBackendRowsLimitError(e)) {
          errorToast({
            messageKey: i18next.t('TREND_ROWS_LIMIT_ERROR'),
          });
        } else {
          errorToast({
            httpResponseOrError: e,
          });
        }
      }
    })
    .finally(() => {
      exposedForTesting.addCapsuleTimeSegments(true);
      exposedForTesting.createStitchDetails().catch((_err) => {});
    });
}

/**
 * Fetches the series that are the results of a live preview (if one is in progress).
 * This is called when scrolling.
 *
 * @returns {Promise} that resolves once the results have been added to the chart
 */
export function fetchPreviewSeries() {
  const previewSeriesDef = sqTrendSeriesStore.previewSeriesDefinition;
  if (previewSeriesDef?.formula) {
    return exposedForTesting.generatePreviewSeries(
      previewSeriesDef.formula,
      previewSeriesDef.parameters!,
      previewSeriesDef.id!,
      previewSeriesDef.color!,
    );
  } else {
    return Promise.resolve();
  }
}

/**
 * Fetches capsules for specified conditions or all conditions in the display range and dispatches them to be
 * displayed on the chart.
 *
 * @param {Object[]} items - Optional array of items currently in the chart that need to be fetched
 * @return {Promise} Resolves when all the capsules have been dispatched.
 */
export function fetchChartCapsules(items?: any[]): Promise<void> {
  const cancellationGroupPrefix = 'chartCapsules';
  const fetchItems = items
    ? _.map(items, (item) => {
        return sqTrendConditionStore.findItem(item.id);
      })
    : sqTrendConditionStore.items;

  if (sqWorksheetStore.view.key !== WORKSHEET_VIEW.TREND) {
    return Promise.resolve();
  }

  return _.chain(fetchItems)
    .reject((item) => isItemRedacted(item))
    .map((condition: any) => {
      const conditionId = getItemId(condition);
      const cancellationGroup = cancellationGroupPrefix + CANCELLATION_GROUP_GUID_SEPARATOR + conditionId;
      const arrivalFetch = beginArrivalFetch(cancellationGroup);
      // Chain view and capsule time requires chart capsules to be fetched even though the conditions
      // themselves might not be shown
      if (
        sqTrendStore.view === TREND_VIEWS.CALENDAR &&
        isHidden({
          item: condition,
        })
      ) {
        setConditionDataStatusHiddenFromTrend(condition.id);
        // Clears the data from the hidden capsule chartItems
        return Promise.resolve({ id: condition.id, capsules: [] });
      }
      setConditionDataStatusLoading(condition.id);
      const { allDecoratedStatColumns, allDecoratedPropertyColumns } = getPropertyAndStatisticsColumns();

      return cancelGroup(cancellationGroup, true)
        .then(() =>
          computeCapsulesWithLimit(
            {
              id: conditionId,
              isUnbounded: condition.isUnbounded,
              range: getDurationDisplayRange(),
              limit: CHART_CAPSULES_LIMIT,
              cancellationGroup,
            },
            allDecoratedPropertyColumns,
            allDecoratedStatColumns,
          ),
        )
        .then((result) =>
          runArrivalCommit(() => {
            exposedForTesting.fetchStatistics(condition);
            setConditionDataStatusPresent(
              _.assign(
                { id: condition.id },
                _.pick(result, ['warningCount', 'warningLogs', 'timingInformation', 'meterInformation']),
              ),
            );

            // Re-read from store; overlay may have changed since the snapshot was captured above
            const current = sqTrendConditionStore.findItem(conditionId) ?? condition;
            const picked = _.pick(current, [
              'id',
              'name',
              'color',
              'selected',
              'lane',
              'lineWidth',
              'overlay',
              'orderInLane',
            ]);
            return _.assign({ capsules: result.capsules }, picked);
          }, arrivalFetch),
        )
        .then((committed) => committed ?? { id: condition.id, capsules: [] })
        .catch((error) => {
          exposedForTesting.catchItemDataFailure(condition.id, cancellationGroup, error);
          // Prevents all the chart capsules from disappearing when just one is having problems
          return Promise.resolve({ id: condition.id, capsules: [] });
        });
    })
    .thru((promises) => Promise.all(promises))
    .value()
    .then((conditions) => {
      const numPixels = Math.min(getStoredChartWidth(), getMaxSeriesPixels());
      writeRuntimeChartCapsules(conditions as CapsuleCalculatedChartItem[], numPixels, !!items);
    })
    .catch((_err) => {});
}

/**
 * Dispatches a command to set properties on a trend item
 *
 * @param {String} id - The id of the item
 * @param {Object} props - The properties to set
 * @param {String} [pushMode] - One of the PUSH constants
 */
export function setTrendItemProps(id: string, props: object | undefined, pushMode?: PushOption) {
  setPropertiesInTrendStores(_.assign({ id }, props), pushMode);
}

function getLineWidth(sampleDisplayOption: SAMPLE_OPTIONS, currentLineWidth: number) {
  const lineWidths = sampleDisplayOption === SAMPLE_OPTIONS.BAR ? BAR_CHART_LINE_WIDTHS : LINE_WIDTHS;

  return _.includes(lineWidths, currentLineWidth) ? currentLineWidth : 1;
}

/**
 * Dispatches a command to set configuration properties on a trend item
 *
 * @param {Object[]} itemsProperties - items to update
 * @param {String} itemsProperties.id - The id of the item
 * @param {Object} itemsProperties.* - The properties to set
 * @param {PushOption} [pushMode] - One of the PUSH constants
 */
export function setCustomizationProps(itemsProperties: WithProperty<{ id: string }>[], pushMode?: PushOption): void {
  const items = _.map(itemsProperties, (payload) => {
    const item = findItemIn(getTrendStores(), payload.id);

    // Reset the lineWidth to a valid option when changing sampleDisplayOption
    const sampleDisplayOption = payload.sampleDisplayOption;
    if (sampleDisplayOption) {
      let currentItem = item;
      if (_.get(item, 'itemType') === ITEM_TYPES.METRIC) {
        currentItem = _.find(findChildrenIn(getTrendStores(), payload.id), [
          'childType',
          ITEM_CHILDREN_TYPES.METRIC_DISPLAY,
        ]);
      }
      const lineWidth = getLineWidth(sampleDisplayOption, _.get(currentItem, 'lineWidth', 1));

      payload = {
        ...payload,
        lineWidth,
      };
    }

    // When changing lanes without a lane reservation, explicitly set overlay to undefined to clear
    // any existing overlay value (having the property undefined vs not having it matters for merging)
    if (payload.lane !== undefined && payload.overlay === undefined && !item?.laneReservation) {
      payload.overlay = undefined;
    }

    // Turning off auto scale freezes the currently displayed extremes, so if neither min or max are set on the
    // item, we need to push the current values.
    if (payload.axisAutoScale === false && payload.yAxisMin === undefined && payload.yAxisMax === undefined && item) {
      payload = { ...payload, ...getFrozenYAxisCustomizations(item) };
    }

    if (item?.itemType === ITEM_TYPES.CONDITION) {
      // Ensure overlay and orderInLane are mutually exclusive for conditions
      if (payload.overlay !== undefined) {
        payload.orderInLane = undefined;
        payload.orderInLanePriority = undefined;
      }
      if (payload.orderInLane !== undefined) {
        payload.overlay = undefined;
        // Clear orderInLanePriority unless explicitly set in the payload
        if (!('orderInLanePriority' in payload)) {
          payload.orderInLanePriority = undefined;
        }
      }

      // Make sure the chart capsules are updated to reflect the new lane/lineWidth
      const props = _.pick(payload, ['id', 'lineWidth', 'lane', 'overlay', 'orderInLane', 'orderInLanePriority']);
      sqCapsuleRuntimeStore.actions.customizeChartItems(props);
      if (_.has(props, 'lineWidth') || _.has(props, 'overlay') || _.has(props, 'orderInLane')) {
        recomputeRuntimeChartItems();
      }
    }

    payload.itemType = item?.itemType;

    return payload;
  });

  setCustomizationsInTrendStores({ items }, pushMode);
}

/**
 * Sets the scopedTo property of an item so it is available globally
 *
 * @param id - the item ID
 *
 * @returns {Promise} a promise that resolves when the scope has been set and item properties have been fetched
 */
export function setTrendGlobalScope(id: string): Promise<void> {
  return sqItemsApi
    .setScope({ id })
    .then(() => {
      exposedForTesting.fetchItemProps(id);
      emitPermissions(sqWorkbenchStore.stateParams.workbookId, sqWorkbenchStore.stateParams.worksheetId, id);
    })
    .catch((error) => {
      errorToast({ httpResponseOrError: error });
    });
}

/**
 * Sets the offset for the capsule panel's list of capsules.
 *
 * @param offset - The offset in the list of capsules
 *
 * @returns a promise that resolves when table capsules have been fetched using the new offset
 */
export function setCapsulePanelOffset(offset: number): Promise<void> {
  flux.dispatch('TREND_SET_CAPSULE_PANEL_OFFSET', { offset });

  return exposedForTesting.fetchTableAndChartCapsules();
}

/**
 * Resets the offset for the capsule panel's list of capsules to zero.
 *
 * @returns a promise that resolves when table capsules have been fetched using the new offset
 */
export function resetCapsulePanelOffset(): Promise<void> {
  flux.dispatch('TREND_RESET_CAPSULE_PANEL_OFFSET');

  return exposedForTesting.fetchTableAndChartCapsules();
}

/**
 * Refreshes the charts and trends in a Workbench Analysis worksheet.
 */
function refreshViews(): void {
  updateLaneDisplay();
  fetchTreemap();
  refreshScatterPlotView();
}

/**
 * Sets the color of an item.
 *
 * @param {String} id - The id of the item to update
 * @param {String} color - The color for the item
 * @param {PushOption} [pushMode] - One of the PUSH constants
 */
export function setTrendItemColor(id: string, color: string | undefined, pushMode?: PushOption): void {
  const item = findItemIn(getTrendStores(), id);
  setColorInTrendStores({ id, color }, pushMode);

  refreshViews();

  if (item?.childType !== ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE) {
    exposedForTesting.setCapsuleTimeColorMode();
  }
}

/**
 * Toggles the dimming of data outside of capsules in capsule time, hidden if false
 */
export function toggleDimDataOutsideCapsules() {
  flux.dispatch('TREND_TOGGLE_DIM_DATA_OUTSIDE_CAPSULES');
  exposedForTesting.addCapsuleTimeSegments(true);
}

/**
 * Toggles the visibility of unselected items
 */
export function toggleHideUnselectedItems() {
  flux.dispatch('TREND_TOGGLE_HIDE_UNSELECTED_ITEMS');
  if (sqTrendStore.hideUnselectedItems) {
    exposedForTesting.removeUnselectedSeriesFromCapsules();

    const items = getAllItems({});
    if (items.some((item) => item.selected)) {
      clearStatisticsForUnselectedItems(items);
    }
  } else {
    exposedForTesting.fetchHiddenTrendData();
    exposedForTesting.fetchTableAndChartCapsules();
  }
  exposedForTesting.fetchAllStatistics(true);
  updateLaneDisplay();
}

/**
 * Toggles the visibility of uncertainty indicators
 */
export function toggleHideUncertainty() {
  flux.dispatch('TREND_TOGGLE_HIDE_UNCERTAINTY');
}

/**
 * Sets visibility of uncertainty indicators
 *
 * @param hideUncertainty - True if uncertainty indicators should be hidden
 */
export function setHideUncertainty(hideUncertainty: boolean) {
  flux.dispatch('TREND_SET_HIDE_UNCERTAINTY', hideUncertainty);
}

function getItemCustomPropertyValuesAndUOMs(fetchedItem: ItemOutputV1): CustomPropertyValuesAndUOMs {
  return _.chain(sqTrendStore.propertyColumns(TREND_PANELS.SERIES))
    .filter(({ key }) => sqTrendStore.isColumnEnabled(TREND_PANELS.SERIES, key))
    .reduce(
      (allProps, { propertyName }) => {
        const property = _.find(fetchedItem.properties, { name: propertyName });
        if (!property?.value) {
          return allProps;
        }

        return _.merge(allProps, {
          properties: { [propertyName]: property?.value },
          propertiesUOM: { [propertyName]: property?.unitOfMeasure },
        });
      },
      { properties: {}, propertiesUOM: {} },
    )
    .omitBy(_.isEmpty)
    .value();
}

/**
 * Fetches additional static properties for an item. This includes properties that are always fetched, such as
 * name, and others that are enabled by the user and stored in the sqTrendStore. Additionally, the assets
 * associated with an item. For a stored item, it will be the asset that is the parent of the item. For a
 * calculated item, it will be the parent asset of the item and the parent assets of all formula arguments used to
 * compute the calculated item.
 *
 * @param itemId - the ID of the item to fetch properties for
 * @param skipDependencies - skip property dependencies. Useful if these have already been loaded
 * @param fetchedProperties - Properties that have been fetched already, as a batch, that can be used in place of
 * individual calls.
 *
 * @param cosmeticOnly
 * @return Promise that will resolve when all the information has been fetched
 */
export function fetchItemProps(
  itemId: string,
  skipDependencies = false,
  fetchedProperties: { contextConditions?: ContextConditionsMap } = {},
  cosmeticOnly = false,
) {
  const item = findItemIn(getTrendStores(), itemId);
  if (!isInWorkbookRouteAndWorkbookLoaded() || !item || item.isChildOf || item.itemType === ITEM_TYPES.SCALING_COLUMN) {
    return Promise.resolve([]);
  }
  const cancellationGroup = `itemProps-${item.id}`;

  if (cosmeticOnly) {
    return sqItemsApi
      .getItemAndAllProperties({ id: item.id }, { cancellationGroup })
      .then(({ data: fetchedItem }) => {
        const numberFormat =
          _.find(fetchedItem.properties, ['name', SeeqNames.Properties.NumberFormat]) ||
          _.find(fetchedItem.properties, ['name', SeeqNames.Properties.SourceNumberFormat]);
        const stringFormat =
          _.find(fetchedItem.properties, ['name', SeeqNames.Properties.StringFormat]) ||
          _.find(fetchedItem.properties, ['name', SeeqNames.Properties.SourceStringFormat]);
        const displayUnitProp = _.find(fetchedItem.properties, ['name', SeeqNames.Properties.DisplayUnit]);

        exposedForTesting.setTrendItemProps(
          item.id,
          {
            formatOptions: {
              format: _.get(numberFormat, 'value'),
              stringFormat: _.get(stringFormat, 'value'),
            } as FormatOptions,
            displayUnit: displayUnitProp?.value ?? null,
          },
          PUSH_IGNORE,
        );
      })
      .catch((response) => exposedForTesting.catchItemDataFailure(item.id, cancellationGroup, response, {}));
  }

  // Command the last fetch request to update so plugins can recognize when a fetch request was made for an item
  stampLastFetchRequest(item.id);

  const chartCapsuleColumn = sqTrendStore.enabledColumns(TREND_PANELS.CHART_CAPSULES);
  const areAssetLaneLabelsOff = sqTrendStore.labelDisplayConfiguration.asset === LABEL_LOCATIONS.OFF;
  const cursorLabelsNeedAsset = sqTrendStore.cursorLabelKeys.includes('asset');
  const skipGetDependencies =
    (isPresentationWorkbookMode() &&
      sqWorksheetStore.view.key !== WORKSHEET_VIEW.TREEMAP && // treemap needs dependencies
      sqWorksheetStore.view.key !== WORKSHEET_VIEW.TABLE && // table builder needs dependencies
      (sqWorksheetStore.view.key !== WORKSHEET_VIEW.TREND ||
        // if we are in a trend, we can still skip dependencies if these labels are off
        (areAssetLaneLabelsOff && !chartCapsuleColumn?.asset && !cursorLabelsNeedAsset))) ||
    item.id.startsWith(DUMMY_ITEM_PREFIX);

  // Fetching dependencies can be slow and we do not want other aspects of the UI (e.g. edit icon) to have to wait
  // for it to finish before rendering.
  const dependenciesPromise = (
    skipGetDependencies
      ? Promise.resolve({ assets: [] })
      : getDependencies({ id: item.id }).catch((response) =>
          exposedForTesting.catchItemDataFailure(item.id, cancellationGroup, response, []),
        )
  ).then(({ assets }) => exposedForTesting.setTrendItemProps(item.id, { assets, includeChildren: true }, PUSH_IGNORE));

  // Always retrieve the item. Calendar and Chain views need the Scorecard and Treemap store
  // rendering information as properties on the item.
  const itemFetchPromise: Promise<ItemOutputV1> = !item.id.startsWith(DUMMY_ITEM_PREFIX)
    ? (sqItemsApi
        .getItemAndAllProperties({ id: item.id }, { cancellationGroup })
        .then(({ data }) => data)
        .catch((response) =>
          exposedForTesting.catchItemDataFailure(item.id, cancellationGroup, response, {}),
        ) as Promise<ItemOutputV1>)
    : Promise.resolve({} as ItemOutputV1);

  return Promise.all([
    itemFetchPromise,
    (item.itemType === ITEM_TYPES.METRIC
      ? fetchMetric(item.id)
          .then((result) => _.get(result, 'data'))
          // A failed metric fetch is already reported by fetchMetric's own catchItemDataFailure call;
          // don't let it reject the rest of the item's properties below.
          .catch(() => undefined)
      : Promise.resolve()) as Promise<ThresholdMetricOutputV1>,
    _.isNil(fetchedProperties.contextConditions) &&
    (item.itemType === ITEM_TYPES.SERIES || item.itemType === ITEM_TYPES.CONDITION)
      ? sqItemsApi
          .getContextCondition({ id: item.id })
          .then(({ data }) => data.id)
          // If it doesn't exist, that's a valid state
          .catch(() => undefined)
      : Promise.resolve(fetchedProperties.contextConditions?.[item.id]),
    resolveScalingTableItemPropsFetch(item),
    itemFetchPromise.then((fetchedItem) => {
      const instanceId = getTemplateInstanceIdFromProps(fetchedItem.properties);
      return instanceId ? getTemplateProps(instanceId, true, item.id) : undefined;
    }),
  ])
    .then(([fetchedItem, fetchedMetric, contextConditionId]) => {
      const dispatchParams: AnyProperty = {};
      dispatchParams.name = fetchedItem.name;

      // We need to retrieve the description every time so that we can add it as an axis/lane label regardless if the
      // column is enabled or not. A deleted description is absent, so blank it rather than keep the stale one
      dispatchParams.description = fetchedItem.description ?? '';

      if (sqTrendStore.isColumnEnabled(TREND_PANELS.SERIES, DATASOURCE_NAME)) {
        dispatchParams.datasource = { name: fetchedItem.datasource?.name };
      }

      // set the formatOptions
      const numberFormat =
        _.find(fetchedItem.properties, ['name', SeeqNames.Properties.NumberFormat]) ||
        _.find(fetchedItem.properties, ['name', SeeqNames.Properties.SourceNumberFormat]);
      const stringFormat =
        _.find(fetchedItem.properties, ['name', SeeqNames.Properties.StringFormat]) ||
        _.find(fetchedItem.properties, ['name', SeeqNames.Properties.SourceStringFormat]);
      dispatchParams.formatOptions = {
        format: _.get(numberFormat, 'value'),
        stringFormat: _.get(stringFormat, 'value'),
      } as FormatOptions;

      // set calculated properties
      dispatchParams.calculationType = getToolType(fetchedItem);

      // Need to know the source id of a swap in a few places such as sq-select-item
      const swapSourceProp = _.find(fetchedItem.properties, ['name', SeeqNames.Properties.SwapSourceId]) as any;
      dispatchParams.swapSourceId = swapSourceProp ? _.toUpper(swapSourceProp.value) : null; // uppercase for
      // consistency with id

      dispatchParams.isArchived = !!fetchedItem.isArchived;

      const uomProp = _.find(
        fetchedItem.properties,
        (prop) => prop.name === SeeqNames.Properties.ValueUom || prop.name === SeeqNames.Properties.Uom,
      ) as any;
      if (uomProp) {
        dispatchParams.valueUnitOfMeasure = uomProp.value;
      }
      const sourceUomProp = _.find(fetchedItem.properties, ['name', SeeqNames.Properties.SourceValueUom]) as any;
      if (sourceUomProp) {
        dispatchParams.sourceValueUnitOfMeasure = sourceUomProp.value;
      }
      const displayUnitProp = _.find(fetchedItem.properties, ['name', SeeqNames.Properties.DisplayUnit]) as any;
      dispatchParams.displayUnit = displayUnitProp?.value ?? null;

      _.assign(dispatchParams, exposedForTesting.getItemCustomPropertyValuesAndUOMs(fetchedItem));

      if (fetchedItem.scopedTo) {
        dispatchParams.scopedTo = fetchedItem.scopedTo;
      }

      if (fetchedItem.effectivePermissions) {
        dispatchParams.effectivePermissions = fetchedItem.effectivePermissions;
      }

      if (item.itemType === ITEM_TYPES.CONDITION) {
        dispatchParams.isUnbounded = !_.some(fetchedItem.properties, ['name', SeeqNames.Properties.MaximumDuration]);
      }

      dispatchParams.contextConditionId = contextConditionId ?? null;

      exposedForTesting.setTrendItemProps(item.id, _.omitBy(dispatchParams, _.isUndefined), PUSH_IGNORE);
      if (!dispatchParams.calculationType) {
        exposedForTesting.setTrendItemProps(item.id, { calculationType: undefined }, PUSH_IGNORE);
      }

      addLegacyBoundaries(item, _.get(item, 'displayedAncillaryItemIds', []));

      if (fetchedMetric) {
        exposedForTesting.addMetricChildren(item.id, fetchedMetric, skipDependencies);
      }

      if (item.itemType === ITEM_TYPES.SERIES) {
        exposedForTesting.addCapsuleTimeSegments(true, [item]);
      }
    })
    .then(() => dependenciesPromise) as Promise<any>;
}

type ScalingTablePropsFetchItem = {
  id: string;
  tableId?: string;
  rowId?: string;
  columnId?: string;
  previousTableId?: string;
  previousRowId?: string;
  previousColumnId?: string;
};

function resolveScalingTableItemPropsFetch(item: ScalingTablePropsFetchItem): Promise<void> {
  if (item.tableId && item.rowId) {
    return getScalingTableItemProps(item.tableId, item.rowId, item.id, item.columnId);
  }
  if (hasPreviousScalingTableInfo(item)) {
    return getScalingTableItemProps(item.previousTableId, item.previousRowId, item.id, item.previousColumnId);
  }
  return Promise.resolve();
}

/**
 * Fetches properties for all items.
 *
 * @param skipDependencies allows property dependencies to be skipped. Useful if these items
 * already have been fetched or are not needed.
 * @returns Resolves when all properties are fetched.
 */
export async function fetchPropsForAllItems(skipDependencies = false) {
  const itemIds = getAllItems({ excludeDummyItems: false }).map((item) => item.id);
  // Need to exclude dummy items from the context condition fetch as they will cause a 404 error, but may still want
  // dummy items to have their properties fetched
  const result = await sqContextApi.getContextConditions({
    itemIds: itemIds.filter((id) => !id.toString().startsWith(DUMMY_ITEM_PREFIX)),
  });
  const contextConditions = (result.data.contextConditions || []).reduce(
    (memo, response) => ({ ...memo, [response.sourceItemId]: response.guid }),
    {} as ContextConditionsMap,
  );
  return Promise.all(
    itemIds.map((id) => exposedForTesting.fetchItemProps(id, skipDependencies, { contextConditions })),
  );
}

/**
 * Helper function to be used in conjunction with fetching data for an item. It sets the item status to "Canceled"
 * or "Failure" based on the response. Because the item status should already be set (e.g. to "Loading"), we only
 * set the cancelled item status if the request was canceled and there is a not new request already in flight and
 * the cancellation call was not flagged as "refetching" which indicates that a data request will follow the
 * cancellation request. The refetching check is needed to prevent unwanted red triangles in the details pane that
 * could occur due to a race condition when the user moved the trend around quickly (causing many cancellations) on
 * a slow network. The race condition was that a prior cancellation promise could resolve after a call to cancel a
 * cancellation group but before the pending requests cancellation interceptor had added the next request (which
 * would result in a no pending requests existing for the cancellation group at that particular moment).
 *
 * @param {String} id - The id of the item
 * @param {String} cancellationGroup - the group name used to count the number of requests
 * @param {Object} error - The error object from the API request
 * @param {any} defaultValue? - An optional return value to be used if specified
 * @returns {Promise|any} returns defaultValue if provided, or otherwise, a rejected promise if a failure status
 * was set on the item
 */
export function catchItemDataFailure(
  id: string,
  cancellationGroup: string,
  error: any,
  defaultValue?: unknown,
): void | Promise<unknown> | unknown {
  const nameWithId = `"${_.get(findItemIn(getTrendStores(), id), 'name', '')}" (${id})`;
  const canceled = isCanceled(error);

  // Show a cancellation "red triangle" in the details pane since this cancellation wasn't expected
  if (canceled && !count(cancellationGroup) && !_.get(error, 'config.refetching', false)) {
    if (_.includes(_.get(error, 'data.statusMessage', ''), SeeqNames.API.ErrorMessages.CancelledDueToTooManyRetries)) {
      setDataStatusCanceledRetriesInTrendStores(id);
    } else {
      setDataStatusCanceledInTrendStores(id);
      // Reset statistics when the request was canceled
      sqItemRuntimeStore.actions.setStatistics(id, {});
    }
    notifyCancellation(`Item data request canceled for ${nameWithId}`);
    // logInfo(`Item data request canceled for ${nameWithId}`);
  } else if (canceled && 'xhrStatus' in error && error?.xhrStatus === 'abort' && error?.config?.refetching) {
    // CRAB-22078: if an item is refetching, sometimes we get stuck in state with an infinite spinner,
    // so tell the user there was a failure
    setDataStatusFailureInTrendStores({ id });
    logWarn(`Item data failure for ${nameWithId}`);
  }

  // Show a "red triangle" in the details pane with more information
  if (!canceled) {
    const message = _.get(error, 'data.statusMessage');
    const errorType = _.get(error, 'data.errorType');
    const errorCategory = _.get(error, 'data.errorCategory');
    const inaccessible = _.get(error, 'data.inaccessible');
    if (errorType === ErrorTypeEnum.MAXDURATIONREQUIRED || errorType === ErrorTypeEnum.MAXDURATIONPROHIBITED) {
      doTrack(TRACK.ERROR, errorType, message);
    }
    if (isForbidden(error)) {
      handleForbidden(error);
      if (_.isNil(inaccessible) || _.includes(inaccessible, id)) {
        setDataStatusRedactedInTrendStores({ id, message, errorType, errorCategory });
        logWarn(`Item redacted ${nameWithId}: ${message}`);
      } else {
        setDataStatusAbortedInTrendStores({ id, message, errorType, errorCategory });
        logWarn(`Item data aborted for ${nameWithId}: ${message}`);
      }
    } else {
      setDataStatusFailureInTrendStores({ id, message, errorType, errorCategory });
      logWarn(`Item data failure for ${nameWithId}: ${message ?? error?.message}`);
    }

    if (_.isNil(defaultValue)) {
      return Promise.reject(error);
    }
  }

  if (!_.isNil(defaultValue)) {
    return defaultValue;
  }
}

/**
 * Toggles whether or not capsule previews are loaded and displayed in the timebar
 *
 * @param capsulePreview - whether or not the capsules should be previewed on investigate range
 */
export function setCapsulePreview(capsulePreview: boolean) {
  flux.dispatch('TREND_SET_CAPSULE_PREVIEW', { capsulePreview });
}

/**
 * @example
 *          ---If you need debounced version of a function, put handle up top like:
 *          let debouncedHandle;
 *
 *          ---then, when you need to use it somewhere:
 *          function foo() {
 *            ...
 *            debouncedHandle =  lazyDebounceOf(debouncedFunction, debouncedHandle);
 *            debouncedHandle();
 *          }
 * @param debounceHandle
 * @param functionToDebounce
 */
export function lazyDebounceOf<T extends (...args: never[]) => unknown>(
  functionToDebounce: T,
  debounceHandle?: ReturnType<typeof _.debounce>,
) {
  // We defer calling _.debounce until the first time it is used so that tests have an opportunity to
  // replace _.debounce. See CRAB-7098
  if (!debounceHandle) {
    debounceHandle = _.debounce(functionToDebounce, DEBOUNCE.MEDIUM) as ReturnType<typeof _.debounce>;
  }
  return debounceHandle;
}

function clearStatisticsForUnselectedItems(items: { selected?: boolean; id: string }[]) {
  items.filter((item) => !item.selected).forEach((item) => sqItemRuntimeStore.actions.setStatistics(item.id, {}));
}

export const exposedForTesting = {
  getChartWidth,
  setCustomizationMode,
  isInWorkbookRouteAndWorkbookLoaded,
  isPresentationWorkbookMode,
  isTrendable,
  fetchItemProps,
  removeColumnFilter,
  setCapsuleTimeColorMode,
  setItemSelected,
  fetchTimeseries,
  fetchTableAndChartCapsules,
  setColumnEnabled,
  fetchTimebarCapsules,
  fetchChartCapsules,
  fetchStatistics,
  setTrendItemProps,
  addTrendItem,
  fetchScalar,
  removeUnselectedSeriesFromCapsules,
  fetchHiddenTrendData,
  fetchItems,
  fetchDataBasedOnView,
  catchItemDataFailure,
  addMetricChildren,
  addCapsuleTimeSegments,
  removeChildren,
  setCustomizationProps,
  setShowSelectedRegionOptions,
  removeItems,
  fetchAllTimebarCapsules,
  removeAllItems,
  removeTrendSelectedRegion,
  createStitchDetails,
  fetchAllTimeseries,
  setTrendCapsuleTimeOffsets,
  fetchSignalSegments,
  updateStatistics,
  fetchAllStatistics,
  setTrendSelectedRegion,
  setColumnFilter,
  removePropertiesColumn,
  setTrendItemColor,
  fetchAllScalars,
  fetchPreviewSeries,
  fetchPropsForAllItems,
  setCapsulePanelOffset,
  setPanelSort,
  fetchAllItems,
  generatePreviewSeries,
  toggleTrendItemSelected,
  unselectAllCapsules,
  removeTrendItem,
  setTrendView,
  setGridlines,
  toggleHideUnselectedItems,
  toggleDimDataOutsideCapsules,
  setLabelDisplayConfiguration,
  getCapsuleCustomPropertyValuesAndUOMs,
  getItemCustomPropertyValuesAndUOMs,
  resetCapsulePanelOffset,
  updateCustomLabelTargets,
  addAnnotation,
  computeStaleness,
};
